import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect, useCallback } from "react";
import { subirDatos, eliminarDato } from "./syncSupabase";
import { llamarARIA } from "./AriaProxy";

const STORAGE_KEY = 'alqos_haccp';
const storage = {
  get: (def=[]) => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)||JSON.stringify(def)); } catch { return def; } },
  set: (v) => { try { localStorage.setItem(STORAGE_KEY,JSON.stringify(v)); } catch {} },
};

const Q = {
  azul:'#1C3FAA', azulVivo:'#2A7FFF', grisClaro:'#E8E9F6',
  grisPalido:'#F4F6FB', grisOscuro:'#2B2F36', grisPlata:'#8E93A1', blanco:'#FFFFFF',
};

const RUBROS_DEFAULT = [
  'Packaging secundario','Alimentos y bebidas','Cosmética y cuidado personal',
  'Farmacéutico','Plásticos alimentarios','Papel y cartón para contacto','Otro (especificar)',
];

const TIPOS_PELIGRO = [
  { id:'B', label:'Biológico 🦠', color:'#e63946', desc:'Bacterias, virus, parásitos, hongos' },
  { id:'Q', label:'Químico ⚗️',  color:'#f59e0b', desc:'Tintas, solventes, metales pesados, alérgenos' },
  { id:'F', label:'Físico 🔩',   color:'#3b82f6', desc:'Fragmentos, cuerpos extraños, partículas' },
  { id:'A', label:'Alergénico ⚠️', color:'#7B5BF2', desc:'Gluten, frutos secos, lácteos, soja' },
];

const ARBOL_PREGUNTAS = [
  { id:'P1', texto:'¿Existen medidas preventivas de control para este peligro?', si:'P2', no:'MODIFICAR' },
  { id:'P2', texto:'¿Este paso está diseñado específicamente para eliminar o reducir el peligro a niveles aceptables?', si:'PCC', no:'P3' },
  { id:'P3', texto:'¿Podría una contaminación ocurrir en exceso de niveles aceptables o aumentar a niveles inaceptables?', si:'P4', no:'NO_PCC' },
  { id:'P4', texto:'¿Un paso posterior eliminará o reducirá el peligro a nivel aceptable?', si:'NO_PCC', no:'PCC' },
];

const ETAPAS_CODEX = [
  { id:1, label:'1. Formación del equipo HACCP', icon:'👥' },
  { id:2, label:'2. Descripción del producto', icon:'📦' },
  { id:3, label:'3. Uso previsto', icon:'🎯' },
  { id:4, label:'4. Diagrama de flujo', icon:'🔀' },
  { id:5, label:'5. Análisis de peligros', icon:'⚠️' },
  { id:6, label:'6. Puntos críticos de control', icon:'🎯' },
  { id:7, label:'7. Monitoreo y registros', icon:'📊' },
  { id:8, label:'8. Informe general', icon:'📋' },
];

function generarCodigo(lista) { return `HACCP-${new Date().getFullYear()}-${String(lista.length+1).padStart(3,'0')}`; }
function fechaHoy() { return new Date().toISOString().slice(0,10); }

export default function HACCP({ onVolver, oscuro, usuario }) {
  const [planes, setPlanes]     = useState(() => storage.get([]));
  const [vista, setVista]       = useState('listado');
  const [planActual, setPlanActual] = useState(null);
  const [etapaActiva, setEtapaActiva] = useState(1);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  // ARIA estados
  const [cargandoARIA, setCargandoARIA] = useState(false);
  const [panelARIA, setPanelARIA]       = useState(null); // 'peligros'|'arbol'|'limites'|'reporte'|'nc'
  const [resultadoARIA, setResultadoARIA] = useState(null);
  const [peligroParaARIA, setPeligroParaARIA] = useState(null);
  const [pccParaARIA, setPccParaARIA]   = useState(null);

  const [form, setForm] = useState({
    rubro: RUBROS_DEFAULT[0], producto:'', descripcion:'', usoPrevisto:'',
    consumidorObjetivo:'', alergenosDeclarados:'', vidaUtil:'', condAlmacenamiento:'',
    equipoHACCP:[], flujo:[], peligros:[], pccs:[], registros:[],
  });

  const [formPeligro, setFormPeligro] = useState({ etapa:'', tipo:'B', descripcion:'', causas:'', severidad:'alta', probabilidad:'media' });
  const [formPCC, setFormPCC]         = useState({ peligroId:'', etapa:'', limCritico:'', limOperacional:'', monitoreo:'', frecuencia:'', responsable:'', accionCorrectiva:'' });
  const [formMiembro, setFormMiembro] = useState({ nombre:'', rol:'', area:'' });
  const [formEtapaFlujo, setFormEtapaFlujo] = useState({ nombre:'', descripcion:'', parametros:'' });
  const [formRegistro, setFormRegistro] = useState({ pccId:'', fecha: fechaHoy(), valor:'', conforme:'si', observaciones:'', operador:'' });

  const [arbolPeligro, setArbolPeligro]       = useState(null);
  const [arbolRespuestas, setArbolRespuestas] = useState({});
  const [arbolResultado, setArbolResultado]   = useState(null);
  const [conclusionARIA, setConclusionARIA]   = useState(null);
  const [cargandoConclusionARIA, setCargandoConclusionARIA] = useState(false);

  // Cargar conclusión guardada cuando se abre un plan
  useEffect(() => {
    if (planActual?.conclusionARIA) {
      setConclusionARIA(planActual.conclusionARIA);
    } else {
      setConclusionARIA(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planActual?.id]);

  const esAdmin = usuario?.rol === 'Administrador';

  const bg     = oscuro ? '#09184D' : Q.grisPalido;
  const cardBg = oscuro ? '#0d2160' : Q.blanco;
  const texto  = oscuro ? '#EDF2FA' : Q.grisOscuro;
  const borde  = oscuro ? '#1e3a8a' : Q.grisClaro;
  const navBg  = oscuro ? '#060f30' : Q.azul;
  const sub    = oscuro ? '#9aabd4' : Q.grisPlata;
  const card   = { background:cardBg, borderRadius:'14px', padding:'1.5rem', border:`1px solid ${borde}`, marginBottom:'12px' };
  const inp    = { width:'100%', padding:'9px 12px', borderRadius:'10px', border:`1.5px solid ${borde}`, fontSize:'13px', boxSizing:'border-box', marginTop:'4px', background:cardBg, color:texto, fontFamily:'sans-serif' };
  const lbl    = { fontSize:'11px', color:sub, fontWeight:'700', display:'block', marginTop:'12px', textTransform:'uppercase', letterSpacing:'.05em' };
  const btn    = (col='#2A7FFF') => ({ padding:'8px 18px', background:col, color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontSize:'13px', fontWeight:'700' });

  // ─── SUPABASE ────────────────────────────────────────────────
  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('haccp').select('data');
        if (data?.length > 0) {
          const todos = data.map(r=>r.data).filter(Boolean);
          if (todos.length > 0) { setPlanes(todos); storage.set(todos); }
        }
      } catch(e) { console.warn('HACCP Supabase:', e); }
    }
    cargar();
  }, []);

  const guardarPlanes = useCallback((nuevos) => {
    setPlanes(nuevos);
    storage.set(nuevos);
    subirDatos(STORAGE_KEY);
  }, []);

  const actualizarPlan = useCallback((cambios) => {
    setPlanActual(prev => {
      const updated = { ...prev, ...cambios };
      setPlanes(ps => {
        const nuevos = ps.map(p => p.id === updated.id ? updated : p);
        storage.set(nuevos);
        subirDatos(STORAGE_KEY);
        return nuevos;
      });
      return updated;
    });
  }, []);

  // ════════════════════════════════════════════════════════════
  // ARIA — FUNCIONES
  // ════════════════════════════════════════════════════════════

  // 1. Generar peligros automáticamente
  const ariaPeligros = async () => {
    if (!planActual) return;
    setCargandoARIA(true);
    setPanelARIA('peligros');
    setResultadoARIA(null);
    try {
      const etapas = (planActual.flujo||[]).map(e=>e.nombre).join(', ') || 'Recepción, Producción, Almacenamiento, Expedición';
      const prompt = `Sos ARIA, experto en inocuidad alimentaria, HACCP y Codex Alimentarius. Analizá el siguiente producto y generá un análisis de peligros completo.

Producto: ${planActual.producto}
Rubro: ${planActual.rubro}
Descripción: ${planActual.descripcion||'No especificada'}
Uso previsto: ${planActual.usoP||'No especificado'}
Alérgenos: ${planActual.alergenosDeclarados||'No declarados'}
Etapas del proceso: ${etapas}

Generá EXACTAMENTE un JSON array con los peligros más relevantes. Máximo 8 peligros. Sin texto antes ni después.
Tipos: B=Biológico, Q=Químico, F=Físico, A=Alergénico
Severidad y probabilidad: alta, media, baja

[{"tipo":"Q","etapa":"Impresión","descripcion":"Migración de tintas no aptas para contacto","causas":"Uso de tintas sin certificación para packaging alimentario","severidad":"alta","probabilidad":"media"},...]`;

      const raw = await llamarARIA(prompt);
      const peligros = JSON.parse(raw);
      setResultadoARIA(peligros);
    } catch(e) {
      console.error(e);
      setResultadoARIA({ error: 'Error al generar peligros. Verificá la API key.' });
    }
    setCargandoARIA(false);
  };

  const aplicarPeligrosARIA = () => {
    if (!Array.isArray(resultadoARIA)) return;
    const nuevos = resultadoARIA.map(p => ({ ...p, id:Date.now()+Math.random(), esPCC:false, resultadoArbol:null, origenARIA:true }));
    actualizarPlan({ peligros: [...(planActual.peligros||[]), ...nuevos] });
    setPanelARIA(null);
    setResultadoARIA(null);
    setEtapaActiva(5);
  };

  // 2. Árbol de decisiones asistido por ARIA
  const ariaArbol = async (peligro) => {
    setCargandoARIA(true);
    setPanelARIA('arbol');
    setPeligroParaARIA(peligro);
    setResultadoARIA(null);
    try {
      const prompt = `Sos ARIA, experto en HACCP Codex Alimentarius. Analizá este peligro usando el árbol de decisiones estándar y determiná si es un PCC.

Peligro: ${peligro.descripcion}
Tipo: ${peligro.tipo === 'B'?'Biológico':peligro.tipo==='Q'?'Químico':peligro.tipo==='F'?'Físico':'Alergénico'}
Etapa: ${peligro.etapa}
Severidad: ${peligro.severidad}
Probabilidad: ${peligro.probabilidad}
Producto: ${planActual.producto} (${planActual.rubro})

Respondé el árbol de decisiones Codex:
P1: ¿Existen medidas preventivas?
P2: ¿El paso está diseñado para eliminar/reducir el peligro?
P3: ¿Podría la contaminación aumentar a niveles inaceptables?
P4: ¿Un paso posterior lo eliminará?

JSON exacto sin texto antes ni después:
{"P1":"si","P2":"no","P3":"si","P4":"no","resultado":"PCC","justificacion":"Explicación técnica breve","limCriticoSugerido":"Valor o parámetro sugerido","accionCorrectivaSugerida":"Qué hacer ante desvío"}`;

      const raw = await llamarARIA(prompt);
      setResultadoARIA(JSON.parse(raw));
    } catch(e) {
      setResultadoARIA({ error: 'Error al analizar el árbol.' });
    }
    setCargandoARIA(false);
  };

  const aplicarArbolARIA = () => {
    if (!resultadoARIA || !peligroParaARIA) return;
    const esPCC = resultadoARIA.resultado === 'PCC';
    const peligrosActualizados = (planActual.peligros||[]).map(p =>
      p.id === peligroParaARIA.id
        ? { ...p, esPCC, resultadoArbol: resultadoARIA.resultado, respuestasArbol: { P1:resultadoARIA.P1, P2:resultadoARIA.P2, P3:resultadoARIA.P3, P4:resultadoARIA.P4 }, justificacionARIA: resultadoARIA.justificacion }
        : p
    );
    actualizarPlan({ peligros: peligrosActualizados });
    // Si es PCC, pre-cargar el form de PCC con sugerencias
    if (esPCC) {
      setFormPCC(f => ({
        ...f,
        peligroId: String(peligroParaARIA.id),
        etapa: peligroParaARIA.etapa,
        limCritico: resultadoARIA.limCriticoSugerido || '',
        accionCorrectiva: resultadoARIA.accionCorrectivaSugerida || '',
      }));
    }
    setPanelARIA(null);
    setResultadoARIA(null);
    setPeligroParaARIA(null);
    setEtapaActiva(esPCC ? 6 : 5);
  };

  // 3. Sugerir límites críticos para un PCC
  const ariaLimites = async (pcc) => {
    setCargandoARIA(true);
    setPanelARIA('limites');
    setPccParaARIA(pcc);
    setResultadoARIA(null);
    try {
      const peligroVinculado = (planActual.peligros||[]).find(p=>String(p.id)===String(pcc.peligroId));
      const prompt = `Sos ARIA, experto en inocuidad y HACCP. Sugerí límites críticos y operacionales para este PCC.

Producto: ${planActual.producto} (${planActual.rubro})
Etapa del PCC: ${pcc.etapa}
Peligro vinculado: ${peligroVinculado?.descripcion || pcc.etapa}
Tipo de peligro: ${peligroVinculado?.tipo || 'Q'}

JSON exacto sin texto antes ni después:
{"limCritico":"valor exacto con unidad","limOperacional":"valor con margen de seguridad","sistemaMonitoreo":"instrumento o método recomendado","frecuencia":"cada cuánto tiempo o por lote","responsable":"cargo recomendado","accionCorrectiva":"qué hacer si se supera el límite crítico","fundamentoNormativo":"norma o referencia técnica que sustenta el límite"}`;

      const raw = await llamarARIA(prompt);
      setResultadoARIA(JSON.parse(raw));
    } catch(e) {
      setResultadoARIA({ error: 'Error al sugerir límites.' });
    }
    setCargandoARIA(false);
  };

  const aplicarLimitesARIA = () => {
    if (!resultadoARIA || !pccParaARIA) return;
    const pccsActualizados = (planActual.pccs||[]).map(p =>
      p.id === pccParaARIA.id
        ? { ...p, limCritico: resultadoARIA.limCritico, limOperacional: resultadoARIA.limOperacional, monitoreo: resultadoARIA.sistemaMonitoreo, frecuencia: resultadoARIA.frecuencia, responsable: resultadoARIA.responsable, accionCorrectiva: resultadoARIA.accionCorrectiva, fundamentoNormativo: resultadoARIA.fundamentoNormativo }
        : p
    );
    actualizarPlan({ pccs: pccsActualizados });
    setPanelARIA(null);
    setResultadoARIA(null);
    setPccParaARIA(null);
  };

  // 4. NC automática desde monitoreo no conforme
  const ariaNcDesdeMonitoreo = async (registro) => {
    setCargandoARIA(true);
    setPanelARIA('nc');
    setResultadoARIA(null);
    try {
      const pcc = (planActual.pccs||[]).find(p=>String(p.id)===String(registro.pccId));
      const prompt = `Sos ARIA, experto en gestión de calidad. Se detectó un desvío en monitoreo HACCP. Generá una No Conformidad estructurada para el sistema ALQOS.

Plan HACCP: ${planActual.codigo} — ${planActual.producto}
PCC afectado: ${pcc?.etapa || registro.pccLabel}
Límite crítico: ${pcc?.limCritico || 'No definido'}
Valor registrado: ${registro.valor}
Operador: ${registro.operador}
Fecha: ${registro.fecha}
Observaciones: ${registro.observaciones || 'Sin observaciones'}

JSON exacto sin texto antes ni después:
{"titulo":"Título corto de la NC","descripcion":"Descripción detallada del desvío","causaRaiz":"Causa raíz probable","accionInmediata":"Qué hacer ahora mismo","accionCorrectiva":"Acción correctiva a mediano plazo","responsable":"Cargo responsable","prioridad":"alta","tipo":"proceso"}`;

      const raw = await llamarARIA(prompt);
      setResultadoARIA({ ...JSON.parse(raw), registro });
    } catch(e) {
      setResultadoARIA({ error: 'Error al generar NC.' });
    }
    setCargandoARIA(false);
  };

  const crearNcDesdeARIA = () => {
    if (!resultadoARIA || resultadoARIA.error) return;
    // Guardar NC en localStorage del módulo NC para que aparezca allá
    try {
      const ncs = JSON.parse(localStorage.getItem('alqos_ncs')||'[]');
      const nueva = {
        id: Date.now(),
        codigo: `NC-${new Date().getFullYear()}-${String(ncs.length+1).padStart(4,'0')}`,
        titulo: resultadoARIA.titulo,
        descripcion: resultadoARIA.descripcion,
        tipo: resultadoARIA.tipo || 'proceso',
        prioridad: resultadoARIA.prioridad || 'alta',
        estado: 'abierta',
        causaRaiz: resultadoARIA.causaRaiz,
        accionInmediata: resultadoARIA.accionInmediata,
        accionCorrectiva: resultadoARIA.accionCorrectiva,
        responsable: resultadoARIA.responsable,
        origen: `HACCP — ${planActual.codigo}`,
        fechaCreacion: new Date().toISOString(),
        creadoPor: usuario?.nombre || 'ARIA',
        origenARIA: true,
      };
      localStorage.setItem('alqos_ncs', JSON.stringify([nueva, ...ncs]));
      subirDatos('alqos_ncs');
      alert(`✅ NC ${nueva.codigo} creada automáticamente y guardada en el módulo de No Conformidades.`);
    } catch(e) {
      alert('Error al crear la NC. Revisá el módulo de NC manualmente.');
    }
    setPanelARIA(null);
    setResultadoARIA(null);
  };

  // ─── ARIA CONCLUSIÓN INFORME ──────────────────────────────────
  const ariaConclusion = async (plan) => {
    setCargandoConclusionARIA(true);
    setConclusionARIA(null);
    const peligros  = plan.peligros||[];
    const pccs      = plan.pccs||[];
    const registros = plan.registros||[];
    const noConformes = registros.filter(r=>r.conforme==='no');
    const tasaConformidad = registros.length > 0
      ? ((registros.filter(r=>r.conforme==='si').length / registros.length) * 100).toFixed(0)
      : null;

    try {
      const prompt = `Sos ARIA, experto en inocuidad alimentaria y auditoría HACCP. Analizá este plan HACCP y generá una conclusión ejecutiva estructurada.

DATOS DEL PLAN:
Producto: ${plan.producto} (${plan.rubro})
Estado: ${plan.estado} · Versión: ${plan.version}
Equipo: ${(plan.equipoHACCP||[]).length} integrantes
Etapas del proceso: ${(plan.flujo||[]).length}
Peligros identificados: ${peligros.length} (${peligros.filter(p=>p.tipo==='B').length} biológicos, ${peligros.filter(p=>p.tipo==='Q').length} químicos, ${peligros.filter(p=>p.tipo==='F').length} físicos, ${peligros.filter(p=>p.tipo==='A').length} alergénicos)
PCCs establecidos: ${pccs.length}
Registros de monitoreo: ${registros.length}
No conformes: ${noConformes.length}
Tasa de conformidad: ${tasaConformidad ? tasaConformidad + '%' : 'Sin datos'}

PCCs detalle:
${pccs.map((p,i)=>`PCC-${i+1}: ${p.etapa} — LC: ${p.limCritico} — Monitor: ${p.monitoreo}`).join('\n')||'Sin PCCs'}

Peligros principales:
${peligros.slice(0,5).map(p=>`[${p.tipo}] ${p.descripcion} — ${p.etapa} — Sev: ${p.severidad}`).join('\n')||'Sin peligros'}

Generá un JSON con la conclusión. Sin texto antes ni después:
{
  "nivelMadurez": "Inicial/En desarrollo/Consolidado/Avanzado",
  "puntuacionGeneral": 75,
  "fortalezas": ["fortaleza 1", "fortaleza 2", "fortaleza 3"],
  "brechas": ["brecha 1", "brecha 2"],
  "riesgoGlobal": "Bajo/Medio/Alto",
  "recomendaciones": [{"prioridad":"alta","accion":"descripción acción","plazo":"inmediato/30 días/90 días"}],
  "aptitudAuditoria": true,
  "comentarioAuditoria": "El plan está/no está listo para auditoría porque...",
  "conclusionGeneral": "Párrafo de conclusión general del plan HACCP en 3-4 oraciones"
}`;

      const raw = await llamarARIA(prompt);
      const conclusion = JSON.parse(raw);
      setConclusionARIA(conclusion);
      actualizarPlan({ conclusionARIA: conclusion });
      // Forzar guardado directo en Supabase
      try {
        const planConclusion = { ...plan, conclusionARIA: conclusion };
        const planesLS = JSON.parse(localStorage.getItem('alqos_haccp') || '[]');
        const nuevosLS = planesLS.map(p => p.id === plan.id ? planConclusion : p);
        localStorage.setItem('alqos_haccp', JSON.stringify(nuevosLS));
        subirDatos('alqos_haccp');
      } catch(e2) { console.error('Error guardando conclusión:', e2); }
    } catch(e) {
      setConclusionARIA({ error: 'Error al generar la conclusión. Verificá la API key.' });
    }
    setCargandoConclusionARIA(false);
  };

  // ─── REPORTE PDF (sin API, datos directos + conclusión ARIA si existe) ──────
  const ariaReporte = () => {
    if (!planActual) return;
    const peligros  = planActual.peligros||[];
    const pccs      = planActual.pccs||[];
    const registros = planActual.registros||[];
    const equipo    = planActual.equipoHACCP||[];
    const flujo     = planActual.flujo||[];
    const noConformes = registros.filter(r=>r.conforme==='no');
    const conformes   = registros.filter(r=>r.conforme==='si');
    const tasa = registros.length>0?((conformes.length/registros.length)*100).toFixed(0):null;
    const fecha = new Date().toLocaleDateString('es-AR');

    const tipoLabel = {B:'Biológico',Q:'Químico',F:'Físico',A:'Alergénico'};
    const sevColor  = {alta:'#e63946',media:'#f59e0b',baja:'#22c55e'};

    const seccionPeligros = peligros.length>0 ? `
      <div class="section">
        <h2>5. Análisis de peligros (${peligros.length} identificados)</h2>
        <table>
          <thead><tr><th>Tipo</th><th>Etapa</th><th>Peligro</th><th>Causas</th><th>Sev.</th><th>Prob.</th><th>PCC</th></tr></thead>
          <tbody>
            ${peligros.map(p=>`<tr>
              <td style="color:${sevColor[p.severidad]||'#333'};font-weight:700">${tipoLabel[p.tipo]||p.tipo}</td>
              <td>${p.etapa||'—'}</td>
              <td>${p.descripcion}</td>
              <td>${p.causas||'—'}</td>
              <td style="color:${sevColor[p.severidad]}">${p.severidad}</td>
              <td>${p.probabilidad}</td>
              <td style="font-weight:700;color:${p.esPCC?'#e63946':'#22c55e'}">${p.esPCC?'✓ PCC':'No PCC'}</td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>` : '';

    const seccionPCCs = pccs.length>0 ? `
      <div class="section">
        <h2>6. Puntos Críticos de Control (${pccs.length} PCCs)</h2>
        ${pccs.map((p,i)=>`
          <div class="pcc-card">
            <div class="pcc-header">🎯 PCC-${i+1}: ${p.etapa}</div>
            <div class="pcc-grid">
              ${p.limCritico?`<div><div class="field-label">Límite crítico</div><div class="field-val red">${p.limCritico}</div></div>`:''}
              ${p.limOperacional?`<div><div class="field-label">Límite operacional</div><div class="field-val orange">${p.limOperacional}</div></div>`:''}
              ${p.monitoreo?`<div><div class="field-label">Sistema de monitoreo</div><div class="field-val">${p.monitoreo}</div></div>`:''}
              ${p.frecuencia?`<div><div class="field-label">Frecuencia</div><div class="field-val">${p.frecuencia}</div></div>`:''}
              ${p.responsable?`<div><div class="field-label">Responsable</div><div class="field-val">${p.responsable}</div></div>`:''}
              ${p.accionCorrectiva?`<div><div class="field-label">Acción correctiva</div><div class="field-val green">${p.accionCorrectiva}</div></div>`:''}
              ${p.fundamentoNormativo?`<div style="grid-column:1/-1"><div class="field-label">Fundamento normativo</div><div class="field-val purple">${p.fundamentoNormativo}</div></div>`:''}
            </div>
          </div>`).join('')}
      </div>` : '';

    const seccionMonitoreo = registros.length>0 ? `
      <div class="section">
        <h2>7. Monitoreo y registros (${registros.length} registros)</h2>
        <div class="kpi-row">
          <div class="kpi"><div class="kpi-val blue">${registros.length}</div><div class="kpi-label">Total</div></div>
          <div class="kpi"><div class="kpi-val green">${conformes.length}</div><div class="kpi-label">Conformes</div></div>
          <div class="kpi"><div class="kpi-val red">${noConformes.length}</div><div class="kpi-label">No conformes</div></div>
          <div class="kpi"><div class="kpi-val ${tasa&&Number(tasa)>=90?'green':tasa&&Number(tasa)>=70?'orange':'red'}">${tasa||'—'}%</div><div class="kpi-label">Conformidad</div></div>
        </div>
        ${noConformes.length>0?`<div class="alert-box">
          <strong>⚠️ No conformidades detectadas:</strong><br>
          ${noConformes.map(r=>`${r.fecha} · ${r.pccLabel} · Valor: ${r.valor}${r.observaciones?' — '+r.observaciones:''}`).join('<br>')}
        </div>`:''}
        <table>
          <thead><tr><th>Fecha</th><th>PCC</th><th>Valor</th><th>Estado</th><th>Operador</th></tr></thead>
          <tbody>
            ${[...registros].slice(-10).reverse().map(r=>`<tr>
              <td>${r.fecha}</td><td>${r.pccLabel||'—'}</td><td><strong>${r.valor}</strong></td>
              <td style="color:${r.conforme==='si'?'#22c55e':'#e63946'};font-weight:700">${r.conforme==='si'?'✅ Conforme':'❌ No conforme'}</td>
              <td>${r.operador||'—'}</td>
            </tr>`).join('')}
          </tbody>
        </table>
        ${registros.length>10?`<p style="font-size:9px;color:#888;margin:4px 0 0">Mostrando últimos 10 de ${registros.length} registros.</p>`:''}
      </div>` : '';

    const seccionConclusionARIA = conclusionARIA && !conclusionARIA.error ? `
      <div class="section aria-section">
        <h2>🤖 Conclusión ARIA — Diagnóstico del plan</h2>
        <div class="aria-kpis">
          <div class="aria-kpi"><div class="aria-score">${conclusionARIA.puntuacionGeneral}/100</div><div class="kpi-label">Puntuación</div></div>
          <div class="aria-kpi"><div class="aria-nivel">${conclusionARIA.nivelMadurez}</div><div class="kpi-label">Nivel de madurez</div></div>
          <div class="aria-kpi"><div class="aria-riesgo ${conclusionARIA.riesgoGlobal==='Bajo'?'green':conclusionARIA.riesgoGlobal==='Medio'?'orange':'red'}">${conclusionARIA.riesgoGlobal}</div><div class="kpi-label">Riesgo global</div></div>
          <div class="aria-kpi"><div class="aria-apt ${conclusionARIA.aptitudAuditoria?'green':'red'}">${conclusionARIA.aptitudAuditoria?'✅ APTO':'⚠️ NO APTO'}</div><div class="kpi-label">Auditoría</div></div>
        </div>
        <div class="conclusion-box"><strong>Conclusión general:</strong><br>${conclusionARIA.conclusionGeneral}</div>
        <div class="conclusion-box" style="background:#f0fdf4;border-color:#22c55e">
          <strong style="color:#15803d">✅ Fortalezas:</strong><br>${(conclusionARIA.fortalezas||[]).map(f=>`• ${f}`).join('<br>')}
        </div>
        ${(conclusionARIA.brechas||[]).length>0?`<div class="conclusion-box" style="background:#fff5f5;border-color:#e63946">
          <strong style="color:#b91c1c">⚠️ Brechas:</strong><br>${conclusionARIA.brechas.map(b=>`• ${b}`).join('<br>')}
        </div>`:''}
        ${(conclusionARIA.recomendaciones||[]).length>0?`
          <strong style="font-size:10px;text-transform:uppercase;color:#555">Recomendaciones:</strong>
          ${conclusionARIA.recomendaciones.map(r=>`<div class="rec-row">
            <span class="badge-${r.prioridad}">${r.prioridad?.toUpperCase()}</span>
            <span>${r.accion}</span>
            <span style="color:#888;font-size:9px">Plazo: ${r.plazo}</span>
          </div>`).join('')}`:''}
      </div>` : `
      <div class="section" style="background:#f5f3ff;border:1px solid #7B5BF240">
        <p style="font-size:11px;color:#7B5BF2;margin:0">💡 <strong>Tip:</strong> Podés agregar el diagnóstico de ARIA al reporte. Andá a la Etapa 8 → Generar conclusión y volvé a exportar.</p>
      </div>`;

    const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8">
<title>Plan HACCP — ${planActual.codigo}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:Arial,sans-serif;font-size:10px;color:#2B2F36}
  .header{background:#1C3FAA;color:white;padding:20px 32px}
  .header h1{font-size:16px;font-weight:800;margin:0 0 3px}
  .header p{font-size:9px;opacity:.75}
  .kpi-bar{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;padding:12px 32px;background:#f4f6fb}
  .kpi{text-align:center;background:white;border-radius:6px;padding:8px;border:1px solid #eee}
  .kpi-val{font-size:16px;font-weight:800;margin-bottom:2px}
  .kpi-label{font-size:8px;color:#888}
  .kpi-row{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:10px}
  .section{padding:16px 32px;border-bottom:1px solid #eee}
  .section h2{font-size:11px;font-weight:700;color:#1C3FAA;text-transform:uppercase;letter-spacing:.08em;margin-bottom:10px}
  table{width:100%;border-collapse:collapse;font-size:9px}
  th{background:#f4f6fb;padding:5px 7px;text-align:left;font-weight:700;color:#555;border-bottom:1px solid #ddd}
  td{padding:4px 7px;border-bottom:.5px solid #eee}
  tr:nth-child(even) td{background:#fafafa}
  .pcc-card{border:1.5px solid #e6394630;border-radius:8px;padding:10px;margin-bottom:8px;background:#fff9f9}
  .pcc-header{font-size:11px;font-weight:800;color:#b91c1c;margin-bottom:8px;padding-bottom:4px;border-bottom:1px solid #e6394630}
  .pcc-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
  .field-label{font-size:8px;color:#888;text-transform:uppercase;font-weight:700;margin-bottom:2px}
  .field-val{font-size:10px;font-weight:600}
  .red{color:#e63946}.orange{color:#f59e0b}.green{color:#22c55e}.blue{color:#2A7FFF}.purple{color:#7B5BF2}
  .alert-box{background:#fff5f5;border:1px solid #e6394640;border-radius:6px;padding:8px;margin-bottom:8px;font-size:9px;color:#b91c1c}
  .aria-section{background:#f5f3ff}
  .aria-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:10px}
  .aria-kpi{text-align:center;background:white;border-radius:6px;padding:8px;border:1px solid #7B5BF240}
  .aria-score{font-size:20px;font-weight:900;color:#7B5BF2}
  .aria-nivel{font-size:11px;font-weight:700;color:#1C3FAA}
  .aria-riesgo,.aria-apt{font-size:11px;font-weight:700}
  .conclusion-box{background:#f0f7ff;border:1px solid #2A7FFF30;border-radius:6px;padding:8px;margin-bottom:6px;font-size:10px;line-height:1.6}
  .rec-row{display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:.5px solid #eee;font-size:9px}
  .badge-alta{background:#fee2e2;color:#b91c1c;padding:1px 6px;border-radius:4px;font-weight:700;font-size:8px;white-space:nowrap}
  .badge-media{background:#fef9c3;color:#a16207;padding:1px 6px;border-radius:4px;font-weight:700;font-size:8px;white-space:nowrap}
  .badge-baja{background:#dcfce7;color:#15803d;padding:1px 6px;border-radius:4px;font-weight:700;font-size:8px;white-space:nowrap}
  .footer{padding:10px 32px;background:#1C3FAA;color:white;display:flex;justify-content:space-between;font-size:8px}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>

<div class="header">
  <h1>🧫 ALQOS™ — Plan HACCP</h1>
  <p>${planActual.codigo} · ${planActual.producto} · ${planActual.rubro} · v${planActual.version} · Estado: ${planActual.estado?.toUpperCase()} · Generado: ${fecha}</p>
</div>

<div class="kpi-bar">
  <div class="kpi"><div class="kpi-val blue">${equipo.length}</div><div class="kpi-label">Equipo</div></div>
  <div class="kpi"><div class="kpi-val" style="color:#7B5BF2">${flujo.length}</div><div class="kpi-label">Etapas proceso</div></div>
  <div class="kpi"><div class="kpi-val orange">${peligros.length}</div><div class="kpi-label">Peligros</div></div>
  <div class="kpi"><div class="kpi-val red">${pccs.length}</div><div class="kpi-label">PCCs</div></div>
  <div class="kpi"><div class="kpi-val green">${registros.length}</div><div class="kpi-label">Registros</div></div>
  <div class="kpi"><div class="kpi-val ${noConformes.length>0?'red':'green'}">${noConformes.length}</div><div class="kpi-label">No conformes</div></div>
</div>

<div class="section">
  <h2>1–3. Producto, uso previsto y equipo</h2>
  <table>
    <tbody>
      ${[['Producto',planActual.producto],['Rubro',planActual.rubro],['Descripción',planActual.descripcion],['Vida útil',planActual.vidaUtil],['Almacenamiento',planActual.condAlmacenamiento],['Alérgenos',planActual.alergenosDeclarados],['Uso previsto',planActual.usoP],['Consumidor objetivo',planActual.consumidorObjetivo]].filter(([,v])=>v).map(([k,v])=>`<tr><td style="font-weight:700;color:#555;width:140px">${k}</td><td>${v}</td></tr>`).join('')}
    </tbody>
  </table>
  ${equipo.length>0?`<div style="margin-top:8px"><strong style="font-size:9px;color:#555;text-transform:uppercase">Equipo HACCP:</strong> ${equipo.map(m=>`${m.nombre} (${m.rol}${m.area?', '+m.area:''})`).join(' · ')}</div>`:''}
</div>

${flujo.length>0?`<div class="section">
  <h2>4. Diagrama de flujo del proceso</h2>
  <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center">
    ${flujo.map((e,i)=>`<span style="background:#eff6ff;border:1px solid #2A7FFF30;border-radius:12px;padding:3px 10px;font-size:9px;font-weight:600">${i+1}. ${e.nombre}${e.parametros?' ('+e.parametros+')':''}</span>${i<flujo.length-1?'<span style="color:#888">→</span>':''}`).join('')}
  </div>
</div>`:''}

${seccionPeligros}
${seccionPCCs}
${seccionMonitoreo}
${seccionConclusionARIA}

<div class="footer">
  <span>ALQOS™ by QORVEX · Plan HACCP · ISO 22000 · Codex Alimentarius CAC/RCP 1-1969</span>
  <span>Generado: ${fecha} · Creado por: ${planActual.creadoPor||'Sistema'}</span>
</div>
</body></html>`;

    try {
      const w = window.open('', '_blank');
      if (!w) { alert('⚠️ El navegador bloqueó la ventana emergente. Permitilas para alqos.vercel.app en la barra de direcciones.'); return; }
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 800);
    } catch(e) {
      alert('Error al abrir el reporte. Verificá que el navegador permita ventanas emergentes.');
    }
  };

  // ─── HELPERS ─────────────────────────────────────────────────
  const crearPlan = () => {
    if (!form.producto?.trim()) { alert('⚠️ El nombre del producto es obligatorio.'); return; }
    const nuevo = { ...form, id:Date.now(), codigo:generarCodigo(planes), fechaCreacion:new Date().toISOString(), creadoPor:usuario?.nombre||'Sistema', estado:'borrador', version:'1.0' };
    const nuevos = [...planes, nuevo];
    guardarPlanes(nuevos);
    setPlanActual(nuevo);
    setEtapaActiva(1);
    setVista('detalle');
  };

  const eliminarPlan = async (id) => {
    const nuevos = planes.filter(p=>p.id!==id);
    guardarPlanes(nuevos);
    await eliminarDato('alqos_haccp', id);
    setConfirmEliminar(null);
    setVista('listado');
    setPlanActual(null);
  };

  // Árbol manual
  const iniciarArbol = (peligro) => { setArbolPeligro(peligro); setArbolRespuestas({}); setArbolResultado(null); setVista('arbol'); };
  const responderArbol = (preguntaId, respuesta) => {
    const nuevas = { ...arbolRespuestas, [preguntaId]:respuesta };
    setArbolRespuestas(nuevas);
    const pregunta = ARBOL_PREGUNTAS.find(p=>p.id===preguntaId);
    const siguiente = respuesta==='si'?pregunta.si:pregunta.no;
    if (['PCC','NO_PCC','MODIFICAR'].includes(siguiente)) setArbolResultado(siguiente);
  };
  const aplicarResultadoArbol = () => {
    if (!arbolResultado||!arbolPeligro||!planActual) return;
    const peligrosActualizados = (planActual.peligros||[]).map(p =>
      p.id===arbolPeligro.id?{...p,esPCC:arbolResultado==='PCC',resultadoArbol:arbolResultado,respuestasArbol:arbolRespuestas}:p
    );
    actualizarPlan({ peligros:peligrosActualizados });
    setVista('detalle');
    setEtapaActiva(6);
  };

  const severidadColor = { alta:'#e63946', media:'#f59e0b', baja:'#22c55e' };
  const riesgoTotal = (sev,prob) => {
    const s=sev==='alta'?3:sev==='media'?2:1, p=prob==='alta'?3:prob==='media'?2:1, r=s*p;
    if(r>=6) return {label:'Riesgo ALTO',color:'#e63946'};
    if(r>=3) return {label:'Riesgo MEDIO',color:'#f59e0b'};
    return {label:'Riesgo BAJO',color:'#22c55e'};
  };

  const Header = ({ titulo, onBack, extra }) => (
    <div style={{background:navBg,padding:'1rem 1.5rem',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:100,boxShadow:'0 2px 12px rgba(0,0,0,.3)',marginBottom:'1.5rem'}}>
      <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
        <button onClick={onBack} style={{background:'rgba(255,255,255,0.1)',border:'none',color:'white',padding:'6px 14px',borderRadius:'10px',cursor:'pointer',fontSize:'13px'}}>← Volver</button>
        <span style={{color:'white',fontWeight:'700',fontSize:'16px'}}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  // ─── PANEL ARIA ───────────────────────────────────────────────
  const PanelARIA = () => {
    if (!panelARIA) return null;
    const colores = { peligros:'#7B5BF2', arbol:'#3b82f6', limites:'#f59e0b', nc:'#e63946', reporte:'#22c55e' };
    const col = colores[panelARIA] || '#7B5BF2';

    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
        <div style={{background:cardBg,borderRadius:'16px',padding:'2rem',maxWidth:'600px',width:'100%',border:`2px solid ${col}`,maxHeight:'80vh',overflowY:'auto'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
            <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
              <span style={{fontSize:'24px'}}>🤖</span>
              <div>
                <p style={{fontSize:'14px',fontWeight:'800',color:col,margin:0}}>ARIA — Asistente HACCP</p>
                <p style={{fontSize:'11px',color:sub,margin:0}}>
                  {panelARIA==='peligros'&&'Análisis automático de peligros'}
                  {panelARIA==='arbol'&&`Árbol de decisiones — ${peligroParaARIA?.descripcion}`}
                  {panelARIA==='limites'&&`Límites críticos — ${pccParaARIA?.etapa}`}
                  {panelARIA==='nc'&&'Generando No Conformidad automática'}
                  {panelARIA==='reporte'&&'Reporte ejecutivo HACCP'}
                </p>
              </div>
            </div>
            <button onClick={()=>{setPanelARIA(null);setResultadoARIA(null);}} style={{background:'none',border:'none',color:sub,cursor:'pointer',fontSize:'20px'}}>×</button>
          </div>

          {cargandoARIA && (
            <div style={{textAlign:'center',padding:'3rem'}}>
              <div style={{fontSize:'40px',marginBottom:'1rem',animation:'spin 1s linear infinite'}}>⚙️</div>
              <p style={{color:col,fontWeight:'700',fontSize:'14px'}}>ARIA está analizando...</p>
              <p style={{color:sub,fontSize:'12px'}}>Esto puede tomar unos segundos</p>
            </div>
          )}

          {!cargandoARIA && resultadoARIA && !resultadoARIA.error && (
            <div>
              {/* PELIGROS */}
              {panelARIA==='peligros' && Array.isArray(resultadoARIA) && (
                <div>
                  <p style={{fontSize:'13px',color:sub,marginBottom:'12px'}}>ARIA identificó {resultadoARIA.length} peligros. Revisalos y confirmalos.</p>
                  {resultadoARIA.map((p,i)=>{
                    const tipo = TIPOS_PELIGRO.find(t=>t.id===p.tipo);
                    const riesgo = riesgoTotal(p.severidad,p.probabilidad);
                    return (
                      <div key={i} style={{padding:'10px 14px',borderRadius:'10px',border:`1px solid ${tipo?.color}40`,marginBottom:'8px',background:oscuro?'#091840':'#f8fafc'}}>
                        <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'4px',flexWrap:'wrap'}}>
                          <span style={{fontSize:'10px',fontWeight:'700',background:`${tipo?.color}22`,color:tipo?.color,padding:'2px 7px',borderRadius:'6px'}}>{tipo?.label}</span>
                          <span style={{fontSize:'10px',fontWeight:'700',background:`${riesgo.color}22`,color:riesgo.color,padding:'2px 7px',borderRadius:'6px'}}>{riesgo.label}</span>
                          <span style={{fontSize:'11px',color:sub}}>{p.etapa}</span>
                        </div>
                        <p style={{fontSize:'13px',color:texto,fontWeight:'600',margin:'0 0 2px'}}>{p.descripcion}</p>
                        <p style={{fontSize:'11px',color:sub,margin:0}}>{p.causas}</p>
                      </div>
                    );
                  })}
                  <button onClick={aplicarPeligrosARIA} style={{...btn('#7B5BF2'),width:'100%',marginTop:'12px',padding:'12px'}}>
                    ✅ Agregar {resultadoARIA.length} peligros al plan
                  </button>
                </div>
              )}

              {/* ÁRBOL */}
              {panelARIA==='arbol' && resultadoARIA.resultado && (
                <div>
                  <div style={{background:resultadoARIA.resultado==='PCC'?(oscuro?'#1a0505':'#fff5f5'):(oscuro?'#0a1f0a':'#f0fdf4'),border:`2px solid ${resultadoARIA.resultado==='PCC'?'#e63946':'#22c55e'}`,borderRadius:'12px',padding:'1.5rem',textAlign:'center',marginBottom:'1rem'}}>
                    <p style={{fontSize:'32px',margin:'0 0 8px'}}>{resultadoARIA.resultado==='PCC'?'🎯':'✅'}</p>
                    <p style={{fontSize:'16px',fontWeight:'800',color:resultadoARIA.resultado==='PCC'?'#e63946':'#22c55e',margin:'0 0 6px'}}>{resultadoARIA.resultado==='PCC'?'PUNTO CRÍTICO DE CONTROL':'No es PCC'}</p>
                    <p style={{fontSize:'12px',color:sub,margin:0}}>{resultadoARIA.justificacion}</p>
                  </div>
                  {[['P1','¿Medidas preventivas?',resultadoARIA.P1],['P2','¿Paso diseñado para eliminar?',resultadoARIA.P2],['P3','¿Contaminación a niveles inaceptables?',resultadoARIA.P3],['P4','¿Paso posterior elimina el peligro?',resultadoARIA.P4]].map(([k,q,r])=>(
                    <div key={k} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 10px',borderRadius:'8px',background:oscuro?'#091840':'#f4f6fb',marginBottom:'6px'}}>
                      <span style={{fontSize:'12px',color:texto}}><strong>{k}:</strong> {q}</span>
                      <span style={{padding:'2px 10px',borderRadius:'6px',fontSize:'11px',fontWeight:'700',background:r==='si'?'#dcfce7':'#fee2e2',color:r==='si'?'#15803d':'#b91c1c'}}>{r==='si'?'SÍ':'NO'}</span>
                    </div>
                  ))}
                  {resultadoARIA.resultado==='PCC' && (
                    <div style={{marginTop:'12px',padding:'10px',borderRadius:'8px',background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #3b82f640'}}>
                      <p style={{fontSize:'11px',color:sub,margin:'0 0 4px',fontWeight:'700',textTransform:'uppercase'}}>Límite crítico sugerido</p>
                      <p style={{fontSize:'13px',color:texto,margin:'0 0 8px'}}>{resultadoARIA.limCriticoSugerido}</p>
                      <p style={{fontSize:'11px',color:sub,margin:'0 0 4px',fontWeight:'700',textTransform:'uppercase'}}>Acción correctiva sugerida</p>
                      <p style={{fontSize:'13px',color:texto,margin:0}}>{resultadoARIA.accionCorrectivaSugerida}</p>
                    </div>
                  )}
                  <button onClick={aplicarArbolARIA} style={{...btn(resultadoARIA.resultado==='PCC'?'#e63946':'#22c55e'),width:'100%',marginTop:'14px',padding:'12px'}}>
                    ✅ Aplicar resultado al plan
                  </button>
                </div>
              )}

              {/* LÍMITES */}
              {panelARIA==='limites' && resultadoARIA.limCritico && (
                <div>
                  {[['Límite crítico',resultadoARIA.limCritico,'#e63946'],['Límite operacional',resultadoARIA.limOperacional,'#f59e0b'],['Sistema de monitoreo',resultadoARIA.sistemaMonitoreo,'#3b82f6'],['Frecuencia',resultadoARIA.frecuencia,sub],['Responsable',resultadoARIA.responsable,sub],['Acción correctiva',resultadoARIA.accionCorrectiva,'#22c55e'],['Fundamento normativo',resultadoARIA.fundamentoNormativo,sub]].map(([k,v,c])=>v&&(
                    <div key={k} style={{padding:'8px 0',borderBottom:`0.5px solid ${borde}`}}>
                      <p style={{fontSize:'10px',color:sub,margin:'0 0 2px',textTransform:'uppercase',fontWeight:'700'}}>{k}</p>
                      <p style={{fontSize:'13px',color:c,fontWeight:'600',margin:0}}>{v}</p>
                    </div>
                  ))}
                  <button onClick={aplicarLimitesARIA} style={{...btn('#f59e0b'),width:'100%',marginTop:'14px',padding:'12px'}}>
                    ✅ Aplicar límites al PCC
                  </button>
                </div>
              )}

              {/* NC */}
              {panelARIA==='nc' && resultadoARIA.titulo && (
                <div>
                  <div style={{padding:'12px',borderRadius:'10px',background:oscuro?'#1a0505':'#fff5f5',border:'1px solid #e6394640',marginBottom:'12px'}}>
                    <p style={{fontSize:'14px',fontWeight:'700',color:'#e63946',margin:'0 0 4px'}}>{resultadoARIA.titulo}</p>
                    <p style={{fontSize:'12px',color:texto,margin:'0 0 8px'}}>{resultadoARIA.descripcion}</p>
                    <p style={{fontSize:'11px',color:sub,margin:'0 0 4px'}}><strong>Causa raíz:</strong> {resultadoARIA.causaRaiz}</p>
                    <p style={{fontSize:'11px',color:sub,margin:'0 0 4px'}}><strong>Acción inmediata:</strong> {resultadoARIA.accionInmediata}</p>
                    <p style={{fontSize:'11px',color:sub,margin:0}}><strong>Acción correctiva:</strong> {resultadoARIA.accionCorrectiva}</p>
                  </div>
                  <button onClick={crearNcDesdeARIA} style={{...btn('#e63946'),width:'100%',padding:'12px'}}>
                    ⚠️ Crear NC automáticamente en el módulo de NC
                  </button>
                </div>
              )}
            </div>
          )}

          {!cargandoARIA && resultadoARIA?.error && (
            <div style={{textAlign:'center',padding:'2rem'}}>
              <p style={{fontSize:'32px'}}>❌</p>
              <p style={{color:'#e63946',fontWeight:'700'}}>{resultadoARIA.error}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════
  // VISTA ÁRBOL MANUAL
  // ════════════════════════════════════════════════════════════
  if (vista==='arbol'&&arbolPeligro) {
    const preguntaActual = ARBOL_PREGUNTAS.find(p=>!arbolRespuestas[p.id]&&!arbolResultado);
    return (
      <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif'}}>
        <Header titulo="🔀 Árbol de decisiones" onBack={()=>setVista('detalle')}/>
        <div style={{padding:'0 2rem 2rem',maxWidth:'700px',margin:'0 auto'}}>
          <div style={{...card,background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #3b82f640',marginBottom:'1.5rem'}}>
            <p style={{fontSize:'11px',color:sub,margin:'0 0 4px',textTransform:'uppercase',fontWeight:'700'}}>Analizando peligro</p>
            <p style={{fontSize:'14px',fontWeight:'700',color:texto,margin:'0 0 2px'}}>{arbolPeligro.descripcion}</p>
            <p style={{fontSize:'12px',color:sub,margin:0}}>{TIPOS_PELIGRO.find(t=>t.id===arbolPeligro.tipo)?.label} · {arbolPeligro.etapa}</p>
          </div>
          {Object.entries(arbolRespuestas).map(([pid,resp])=>{
            const preg=ARBOL_PREGUNTAS.find(p=>p.id===pid);
            return (
              <div key={pid} style={{display:'flex',alignItems:'center',gap:'12px',padding:'10px 14px',borderRadius:'10px',background:oscuro?'#091840':'#f8fafc',border:`1px solid ${borde}`,marginBottom:'8px',opacity:0.7}}>
                <span style={{fontSize:'11px',fontWeight:'700',color:sub,minWidth:'24px'}}>{pid}</span>
                <span style={{fontSize:'12px',color:texto,flex:1}}>{preg?.texto}</span>
                <span style={{padding:'3px 10px',borderRadius:'8px',fontSize:'11px',fontWeight:'700',background:resp==='si'?'#dcfce7':'#fee2e2',color:resp==='si'?'#15803d':'#b91c1c'}}>{resp==='si'?'SÍ':'NO'}</span>
              </div>
            );
          })}
          {arbolResultado?(
            <div style={{...card,background:arbolResultado==='PCC'?(oscuro?'#1a0505':'#fff5f5'):(oscuro?'#0a1f0a':'#f0fdf4'),border:`2px solid ${arbolResultado==='PCC'?'#e63946':'#22c55e'}`,textAlign:'center',padding:'2rem'}}>
              <p style={{fontSize:'48px',margin:'0 0 1rem'}}>{arbolResultado==='PCC'?'🎯':'✅'}</p>
              <p style={{fontSize:'20px',fontWeight:'800',color:arbolResultado==='PCC'?'#e63946':'#22c55e',margin:'0 0 8px'}}>{arbolResultado==='PCC'?'PUNTO CRÍTICO DE CONTROL':arbolResultado==='NO_PCC'?'No es PCC':'Modificar proceso'}</p>
              <button onClick={aplicarResultadoArbol} style={btn(arbolResultado==='PCC'?'#e63946':'#22c55e')}>✅ Aplicar resultado</button>
            </div>
          ):preguntaActual?(
            <div style={{...card,border:'2px solid #2A7FFF',padding:'2rem'}}>
              <p style={{fontSize:'11px',color:Q.azulVivo,fontWeight:'700',margin:'0 0 8px',textTransform:'uppercase'}}>{preguntaActual.id}</p>
              <p style={{fontSize:'15px',fontWeight:'700',color:texto,margin:'0 0 1.5rem',lineHeight:1.5}}>{preguntaActual.texto}</p>
              <div style={{display:'flex',gap:'12px',justifyContent:'center'}}>
                <button onClick={()=>responderArbol(preguntaActual.id,'si')} style={{...btn('#22c55e'),padding:'12px 32px',fontSize:'15px'}}>✅ SÍ</button>
                <button onClick={()=>responderArbol(preguntaActual.id,'no')} style={{...btn('#e63946'),padding:'12px 32px',fontSize:'15px'}}>❌ NO</button>
              </div>
            </div>
          ):null}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // VISTA MONITOREO
  // ════════════════════════════════════════════════════════════
  if (vista==='monitoreo'&&planActual) {
    const pccs = planActual.pccs||[];
    const registros = planActual.registros||[];
    const noConformes = registros.filter(r=>r.conforme==='no');

    const agregarRegistro = () => {
      if (!formRegistro.pccId||!formRegistro.valor) return;
      const pcc = pccs.find(p=>String(p.id)===String(formRegistro.pccId));
      const nuevo = { ...formRegistro, id:Date.now(), pccLabel:pcc?.etapa||'', fecha:formRegistro.fecha||fechaHoy(), operador:formRegistro.operador||usuario?.nombre||'' };
      const nuevosReg = [...registros, nuevo];
      actualizarPlan({ registros: nuevosReg });
      // Si no conforme → ofrecer NC automática
      if (nuevo.conforme==='no') {
        setTimeout(() => {
          if (window.confirm('⚠️ Registro NO CONFORME detectado. ¿Querés que ARIA genere una No Conformidad automáticamente?')) {
            ariaNcDesdeMonitoreo(nuevo);
          }
        }, 300);
      }
      setFormRegistro({ pccId:'', fecha:fechaHoy(), valor:'', conforme:'si', observaciones:'', operador:'' });
    };

    return (
      <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif'}}>
        <PanelARIA/>
        <Header titulo={`📊 Monitoreo — ${planActual.codigo}`} onBack={()=>setVista('detalle')}/>
        <div style={{padding:'0 2rem 2rem',maxWidth:'800px',margin:'0 auto'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:'10px',marginBottom:'1.5rem'}}>
            {[['Total registros',registros.length,'#2A7FFF'],['PCCs activos',pccs.length,'#7B5BF2'],['No conformes',noConformes.length,'#e63946'],['Tasa conform.',registros.length?`${((registros.filter(r=>r.conforme==='si').length/registros.length)*100).toFixed(0)}%`:'—','#22c55e']].map(([l,v,c])=>(
              <div key={l} style={{background:cardBg,borderRadius:'12px',padding:'1rem',border:`1px solid ${borde}`,textAlign:'center'}}>
                <p style={{fontSize:'20px',fontWeight:'800',color:c,margin:'0 0 4px'}}>{v}</p>
                <p style={{fontSize:'10px',color:sub,margin:0}}>{l}</p>
              </div>
            ))}
          </div>

          {noConformes.length>0&&(
            <div style={{background:oscuro?'#1a0505':'#fff5f5',border:'2px solid #e63946',borderRadius:'12px',padding:'12px 16px',marginBottom:'1rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <p style={{fontSize:'13px',fontWeight:'700',color:'#e63946',margin:0}}>⚠️ {noConformes.length} registro{noConformes.length>1?'s':''} no conforme{noConformes.length>1?'s':''}</p>
                <p style={{fontSize:'11px',color:sub,margin:0}}>ARIA puede generar NCs automáticamente</p>
              </div>
              <button onClick={()=>ariaNcDesdeMonitoreo(noConformes[noConformes.length-1])} style={btn('#e63946')}>🤖 Generar NC</button>
            </div>
          )}

          <div style={{...card,background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #2A7FFF40'}}>
            <h3 style={{fontSize:'14px',fontWeight:'700',color:Q.azulVivo,marginBottom:'12px'}}>+ Nuevo registro</h3>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
              <div>
                <span style={lbl}>PCC *</span>
                <select value={formRegistro.pccId} onChange={e=>setFormRegistro(f=>({...f,pccId:e.target.value}))} style={inp}>
                  <option value="">— Seleccioná —</option>
                  {pccs.map((p,i)=><option key={p.id} value={p.id}>PCC-{i+1} · {p.etapa}</option>)}
                </select>
              </div>
              <div>
                <span style={lbl}>Fecha *</span>
                <input type="date" value={formRegistro.fecha} onChange={e=>setFormRegistro(f=>({...f,fecha:e.target.value}))} style={inp}/>
              </div>
              <div>
                <span style={lbl}>Valor medido *</span>
                <input value={formRegistro.valor} onChange={e=>setFormRegistro(f=>({...f,valor:e.target.value}))} style={inp} placeholder="Ej: 72°C, pH 4.5"/>
              </div>
              <div>
                <span style={lbl}>¿Conforme?</span>
                <div style={{display:'flex',gap:'8px',marginTop:'6px'}}>
                  {[['si','✅ Sí','#22c55e'],['no','❌ No','#e63946']].map(([val,lab,col])=>(
                    <button key={val} onClick={()=>setFormRegistro(f=>({...f,conforme:val}))}
                      style={{flex:1,padding:'8px',borderRadius:'8px',border:`2px solid ${formRegistro.conforme===val?col:borde}`,background:formRegistro.conforme===val?`${col}22`:cardBg,color:formRegistro.conforme===val?col:sub,cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>
                      {lab}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <span style={lbl}>Operador</span>
                <input value={formRegistro.operador} onChange={e=>setFormRegistro(f=>({...f,operador:e.target.value}))} style={inp} placeholder={usuario?.nombre}/>
              </div>
              <div>
                <span style={lbl}>Observaciones</span>
                <input value={formRegistro.observaciones} onChange={e=>setFormRegistro(f=>({...f,observaciones:e.target.value}))} style={inp}/>
              </div>
            </div>
            <button onClick={agregarRegistro} style={{...btn(),marginTop:'12px'}}>+ Guardar registro</button>
          </div>

          {registros.length===0?(
            <div style={{textAlign:'center',padding:'3rem',color:sub}}><p style={{fontSize:'32px'}}>📋</p><p>Sin registros todavía</p></div>
          ):[...registros].reverse().map(r=>(
            <div key={r.id} style={{...card,borderLeft:`4px solid ${r.conforme==='si'?'#22c55e':'#e63946'}`}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div>
                  <div style={{display:'flex',gap:'8px',marginBottom:'4px'}}>
                    <span style={{fontSize:'11px',fontWeight:'700',padding:'2px 8px',borderRadius:'6px',background:r.conforme==='si'?'#dcfce7':'#fee2e2',color:r.conforme==='si'?'#15803d':'#b91c1c'}}>{r.conforme==='si'?'✅ CONFORME':'❌ NO CONFORME'}</span>
                    <span style={{fontSize:'12px',color:sub}}>{r.fecha}</span>
                  </div>
                  <p style={{fontSize:'13px',fontWeight:'600',color:texto,margin:'0 0 2px'}}>{r.pccLabel} — <strong style={{color:Q.azulVivo}}>{r.valor}</strong></p>
                  <p style={{fontSize:'11px',color:sub,margin:0}}>{r.operador||'—'} {r.observaciones&&`· ${r.observaciones}`}</p>
                </div>
                <div style={{display:'flex',gap:'6px'}}>
                  {r.conforme==='no'&&<button onClick={()=>ariaNcDesdeMonitoreo(r)} style={{padding:'4px 10px',background:'#e6394622',border:'1px solid #e63946',borderRadius:'8px',cursor:'pointer',fontSize:'11px',color:'#e63946',fontWeight:'700'}}>🤖 NC</button>}
                  <button onClick={()=>actualizarPlan({registros:(planActual.registros||[]).filter(x=>x.id!==r.id)})} style={{background:'none',border:'none',color:'#e63946',cursor:'pointer',fontSize:'16px'}}>×</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // DETALLE PLAN
  // ════════════════════════════════════════════════════════════
  if (vista==='detalle'&&planActual) {
    const peligros = planActual.peligros||[];
    const pccs     = planActual.pccs||[];
    const equipo   = planActual.equipoHACCP||[];
    const flujo    = planActual.flujo||[];
    const peligrosPCC = peligros.filter(p=>p.esPCC);
    const estadoColor = { borrador:'#f59e0b', vigente:'#22c55e', revision:'#3b82f6' };

    const agregarMiembro = () => { if(!formMiembro.nombre.trim())return; actualizarPlan({equipoHACCP:[...equipo,{...formMiembro,id:Date.now()}]}); setFormMiembro({nombre:'',rol:'',area:''}); };
    const agregarEtapaFlujo = () => { if(!formEtapaFlujo.nombre.trim())return; actualizarPlan({flujo:[...flujo,{...formEtapaFlujo,id:Date.now(),orden:flujo.length+1}]}); setFormEtapaFlujo({nombre:'',descripcion:'',parametros:''}); };
    const agregarPeligro = () => {
      if(!formPeligro.descripcion.trim()||!formPeligro.etapa.trim())return;
      actualizarPlan({peligros:[...peligros,{...formPeligro,id:Date.now(),esPCC:false,resultadoArbol:null}]});
      setFormPeligro({etapa:'',tipo:'B',descripcion:'',causas:'',severidad:'alta',probabilidad:'media'});
    };
    const agregarPCC = () => {
      if(!formPCC.etapa.trim()||!formPCC.limCritico.trim())return;
      actualizarPlan({pccs:[...pccs,{...formPCC,id:Date.now(),numeroPCC:`PCC${pccs.length+1}`}]});
      setFormPCC({peligroId:'',etapa:'',limCritico:'',limOperacional:'',monitoreo:'',frecuencia:'',responsable:'',accionCorrectiva:''});
    };

    return (
      <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif'}}>
        <PanelARIA/>
        <Header
          titulo={`${planActual.codigo} — ${planActual.producto}`}
          onBack={()=>{setVista('listado');setPlanActual(null);}}
          extra={
            <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
              <select value={planActual.estado} onChange={e=>actualizarPlan({estado:e.target.value})}
                style={{padding:'5px 10px',borderRadius:'8px',border:`1px solid ${estadoColor[planActual.estado]}`,background:`${estadoColor[planActual.estado]}22`,color:estadoColor[planActual.estado],fontSize:'11px',fontWeight:'700',cursor:'pointer'}}>
                <option value="borrador">📝 Borrador</option>
                <option value="vigente">✅ Vigente</option>
                <option value="revision">🔄 Revisión</option>
              </select>
              {/* Botones ARIA */}
              <button onClick={ariaPeligros} disabled={cargandoARIA}
                style={{padding:'6px 10px',background:'linear-gradient(135deg,#7B5BF2,#1C3FAA)',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'11px',color:'white',fontWeight:'600'}}>
                🤖 ARIA Peligros
              </button>
              <button onClick={ariaReporte} disabled={cargandoARIA}
                style={{padding:'6px 10px',background:'rgba(34,197,94,.2)',border:'1px solid rgba(34,197,94,.4)',borderRadius:'8px',cursor:'pointer',fontSize:'11px',color:'#22c55e',fontWeight:'600'}}>
                📄 Reporte PDF
              </button>
              <button onClick={()=>setVista('monitoreo')}
                style={{padding:'6px 12px',background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',color:'white',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>
                📊 Monitoreo
              </button>
              {esAdmin&&<button onClick={()=>setConfirmEliminar(planActual.id)} style={{padding:'6px 12px',background:'rgba(230,57,70,.2)',border:'1px solid rgba(230,57,70,.4)',color:'#e63946',borderRadius:'8px',cursor:'pointer',fontSize:'12px'}}>🗑</button>}
            </div>
          }
        />

        <div style={{padding:'0 2rem 2rem',maxWidth:'860px',margin:'0 auto'}}>
          <div style={{display:'flex',gap:'4px',marginBottom:'1.5rem',overflowX:'auto',paddingBottom:'4px'}}>
            {ETAPAS_CODEX.map(e=>(
              <button key={e.id} onClick={()=>setEtapaActiva(e.id)}
                style={{padding:'7px 10px',borderRadius:'8px',border:'none',cursor:'pointer',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap',background:etapaActiva===e.id?Q.azulVivo:'transparent',color:etapaActiva===e.id?'white':sub,flexShrink:0}}>
                {e.icon} {e.id}
              </button>
            ))}
          </div>
          <p style={{fontSize:'13px',fontWeight:'700',color:texto,marginBottom:'1rem'}}>{ETAPAS_CODEX.find(e=>e.id===etapaActiva)?.label}</p>

          {/* ETAPA 1 */}
          {etapaActiva===1&&(
            <div style={card}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr auto',gap:'8px',alignItems:'end'}}>
                <div><span style={lbl}>Nombre *</span><input value={formMiembro.nombre} onChange={e=>setFormMiembro(f=>({...f,nombre:e.target.value}))} style={inp} placeholder="Nombre completo"/></div>
                <div><span style={lbl}>Rol HACCP</span><input value={formMiembro.rol} onChange={e=>setFormMiembro(f=>({...f,rol:e.target.value}))} style={inp} placeholder="Líder, Calidad..."/></div>
                <div><span style={lbl}>Área</span><input value={formMiembro.area} onChange={e=>setFormMiembro(f=>({...f,area:e.target.value}))} style={inp} placeholder="Producción, QA..."/></div>
                <button onClick={agregarMiembro} style={{...btn(),alignSelf:'flex-end',padding:'9px 16px'}}>+</button>
              </div>
              {equipo.map(m=>(
                <div key={m.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',borderRadius:'8px',background:oscuro?'#091840':'#f4f6fb',border:`1px solid ${borde}`,marginTop:'8px'}}>
                  <div><span style={{fontSize:'13px',fontWeight:'600',color:texto}}>{m.nombre}</span><span style={{fontSize:'11px',color:sub,marginLeft:'8px'}}>{m.rol} · {m.area}</span></div>
                  <button onClick={()=>actualizarPlan({equipoHACCP:equipo.filter(x=>x.id!==m.id)})} style={{background:'none',border:'none',color:'#e63946',cursor:'pointer',fontSize:'16px'}}>×</button>
                </div>
              ))}
            </div>
          )}

          {/* ETAPA 2 */}
          {etapaActiva===2&&(
            <div style={card}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <div><span style={lbl}>Nombre del producto *</span><input value={planActual.producto} onChange={e=>actualizarPlan({producto:e.target.value})} style={inp}/></div>
                <div><span style={lbl}>Rubro</span><select value={planActual.rubro} onChange={e=>actualizarPlan({rubro:e.target.value})} style={inp}>{RUBROS_DEFAULT.map(r=><option key={r} value={r}>{r}</option>)}</select></div>
                <div style={{gridColumn:'1/-1'}}><span style={lbl}>Descripción</span><textarea rows={3} value={planActual.descripcion||''} onChange={e=>actualizarPlan({descripcion:e.target.value})} style={{...inp,resize:'none'}}/></div>
                <div><span style={lbl}>Vida útil</span><input value={planActual.vidaUtil||''} onChange={e=>actualizarPlan({vidaUtil:e.target.value})} style={inp} placeholder="Ej: 24 meses"/></div>
                <div><span style={lbl}>Condiciones almacenamiento</span><input value={planActual.condAlmacenamiento||''} onChange={e=>actualizarPlan({condAlmacenamiento:e.target.value})} style={inp}/></div>
                <div style={{gridColumn:'1/-1'}}><span style={lbl}>Alérgenos declarados</span><input value={planActual.alergenosDeclarados||''} onChange={e=>actualizarPlan({alergenosDeclarados:e.target.value})} style={inp}/></div>
              </div>
            </div>
          )}

          {/* ETAPA 3 */}
          {etapaActiva===3&&(
            <div style={card}>
              <span style={lbl}>Uso previsto</span>
              <textarea rows={3} value={planActual.usoP||''} onChange={e=>actualizarPlan({usoP:e.target.value})} style={{...inp,resize:'none'}} placeholder="¿Cómo se usará el producto?"/>
              <span style={lbl}>Consumidor objetivo</span>
              <textarea rows={2} value={planActual.consumidorObjetivo||''} onChange={e=>actualizarPlan({consumidorObjetivo:e.target.value})} style={{...inp,resize:'none'}}/>
            </div>
          )}

          {/* ETAPA 4 */}
          {etapaActiva===4&&(
            <div>
              <div style={card}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr auto',gap:'8px',alignItems:'end'}}>
                  <div><span style={lbl}>Etapa *</span><input value={formEtapaFlujo.nombre} onChange={e=>setFormEtapaFlujo(f=>({...f,nombre:e.target.value}))} style={inp} placeholder="Ej: Impresión offset"/></div>
                  <div><span style={lbl}>Parámetros</span><input value={formEtapaFlujo.parametros} onChange={e=>setFormEtapaFlujo(f=>({...f,parametros:e.target.value}))} style={inp} placeholder="Temperatura, presión..."/></div>
                  <button onClick={agregarEtapaFlujo} style={{...btn(),alignSelf:'flex-end',padding:'9px 16px'}}>+</button>
                </div>
              </div>
              {flujo.map((e,i)=>(
                <div key={e.id} style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'8px'}}>
                  <div style={{width:'32px',height:'32px',borderRadius:'50%',background:Q.azulVivo,color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',fontWeight:'800',flexShrink:0}}>{i+1}</div>
                  <div style={{flex:1,padding:'10px 14px',borderRadius:'10px',background:oscuro?'#091840':'#f4f6fb',border:`1px solid ${borde}`}}>
                    <p style={{fontSize:'13px',fontWeight:'600',color:texto,margin:'0 0 2px'}}>{e.nombre}</p>
                    {e.parametros&&<p style={{fontSize:'11px',color:sub,margin:0}}>{e.parametros}</p>}
                  </div>
                  <button onClick={()=>actualizarPlan({flujo:flujo.filter(x=>x.id!==e.id)})} style={{background:'none',border:'none',color:'#e63946',cursor:'pointer',fontSize:'16px'}}>×</button>
                </div>
              ))}
            </div>
          )}

          {/* ETAPA 5 — ANÁLISIS PELIGROS + ARIA */}
          {etapaActiva===5&&(
            <div>
              {/* Botón ARIA prominente */}
              <div style={{...card,background:oscuro?'#0a0a2a':'#f5f3ff',border:'2px solid #7B5BF2',marginBottom:'1rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <p style={{fontSize:'13px',fontWeight:'700',color:'#7B5BF2',margin:'0 0 2px'}}>🤖 ARIA — Análisis automático de peligros</p>
                  <p style={{fontSize:'11px',color:sub,margin:0}}>ARIA analiza el producto y genera los peligros probables por tipo y etapa</p>
                </div>
                <button onClick={ariaPeligros} disabled={cargandoARIA}
                  style={{...btn('#7B5BF2'),whiteSpace:'nowrap',flexShrink:0}}>
                  {cargandoARIA?'⏳ Analizando...':'🤖 Generar peligros'}
                </button>
              </div>

              <div style={card}>
                <h3 style={{fontSize:'13px',fontWeight:'700',color:Q.azul,marginBottom:'12px'}}>+ Peligro manual</h3>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <div>
                    <span style={lbl}>Etapa *</span>
                    <select value={formPeligro.etapa} onChange={e=>setFormPeligro(f=>({...f,etapa:e.target.value}))} style={inp}>
                      <option value="">— Seleccioná —</option>
                      {flujo.map(e=><option key={e.id} value={e.nombre}>{e.nombre}</option>)}
                      <option value="Recepción">Recepción MP</option>
                      <option value="Almacenamiento">Almacenamiento</option>
                      <option value="Expedición">Expedición</option>
                    </select>
                  </div>
                  <div>
                    <span style={lbl}>Tipo</span>
                    <div style={{display:'flex',gap:'4px',marginTop:'6px',flexWrap:'wrap'}}>
                      {TIPOS_PELIGRO.map(t=>(
                        <button key={t.id} onClick={()=>setFormPeligro(f=>({...f,tipo:t.id}))}
                          style={{padding:'4px 8px',borderRadius:'6px',fontSize:'10px',cursor:'pointer',fontWeight:'700',border:`2px solid ${formPeligro.tipo===t.id?t.color:borde}`,background:formPeligro.tipo===t.id?`${t.color}22`:cardBg,color:formPeligro.tipo===t.id?t.color:sub}}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{gridColumn:'1/-1'}}><span style={lbl}>Descripción *</span><input value={formPeligro.descripcion} onChange={e=>setFormPeligro(f=>({...f,descripcion:e.target.value}))} style={inp}/></div>
                  <div><span style={lbl}>Causas</span><input value={formPeligro.causas} onChange={e=>setFormPeligro(f=>({...f,causas:e.target.value}))} style={inp}/></div>
                  <div>
                    <span style={lbl}>Severidad / Probabilidad</span>
                    <div style={{display:'flex',gap:'4px',marginTop:'6px'}}>
                      {['alta','media','baja'].map(s=>(
                        <button key={s} onClick={()=>setFormPeligro(f=>({...f,severidad:s}))}
                          style={{flex:1,padding:'5px',borderRadius:'6px',fontSize:'10px',cursor:'pointer',fontWeight:'700',border:`2px solid ${formPeligro.severidad===s?severidadColor[s]:borde}`,background:formPeligro.severidad===s?`${severidadColor[s]}22`:cardBg,color:formPeligro.severidad===s?severidadColor[s]:sub}}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <button onClick={agregarPeligro} style={{...btn(),marginTop:'12px'}}>+ Agregar</button>
              </div>

              {peligros.length===0?(
                <div style={{textAlign:'center',padding:'2rem',color:sub}}><p style={{fontSize:'32px'}}>⚠️</p><p>Sin peligros. Usá ARIA para generarlos.</p></div>
              ):peligros.map(p=>{
                const tipo=TIPOS_PELIGRO.find(t=>t.id===p.tipo);
                const riesgo=riesgoTotal(p.severidad,p.probabilidad);
                return (
                  <div key={p.id} style={{...card,borderLeft:`4px solid ${tipo?.color||borde}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                      <div style={{flex:1}}>
                        <div style={{display:'flex',gap:'6px',marginBottom:'4px',flexWrap:'wrap'}}>
                          <span style={{fontSize:'10px',fontWeight:'700',background:`${tipo?.color}22`,color:tipo?.color,padding:'2px 7px',borderRadius:'6px'}}>{tipo?.label}</span>
                          <span style={{fontSize:'10px',fontWeight:'700',background:`${riesgo.color}22`,color:riesgo.color,padding:'2px 7px',borderRadius:'6px'}}>{riesgo.label}</span>
                          {p.esPCC&&<span style={{fontSize:'10px',fontWeight:'700',background:'#fee2e2',color:'#b91c1c',padding:'2px 7px',borderRadius:'6px'}}>🎯 PCC</span>}
                          {p.origenARIA&&<span style={{fontSize:'9px',fontWeight:'700',background:'linear-gradient(135deg,#7B5BF2,#1C3FAA)',color:'white',padding:'1px 6px',borderRadius:'4px'}}>ARIA</span>}
                        </div>
                        <p style={{fontSize:'13px',fontWeight:'600',color:texto,margin:'0 0 2px'}}>{p.descripcion}</p>
                        <p style={{fontSize:'11px',color:sub,margin:0}}>Etapa: {p.etapa} {p.causas&&`· ${p.causas}`}</p>
                        {p.justificacionARIA&&<p style={{fontSize:'11px',color:'#7B5BF2',margin:'4px 0 0',fontStyle:'italic'}}>🤖 {p.justificacionARIA}</p>}
                      </div>
                      <div style={{display:'flex',gap:'6px',marginLeft:'8px'}}>
                        {!p.resultadoArbol&&(
                          <>
                            <button onClick={()=>ariaArbol(p)} style={{padding:'4px 8px',background:'#7B5BF222',border:'1px solid #7B5BF2',borderRadius:'6px',cursor:'pointer',fontSize:'10px',color:'#7B5BF2',fontWeight:'700'}}>🤖 ARIA</button>
                            <button onClick={()=>iniciarArbol(p)} style={{padding:'4px 8px',background:`${Q.azulVivo}22`,border:`1px solid ${Q.azulVivo}`,borderRadius:'6px',cursor:'pointer',fontSize:'10px',color:Q.azulVivo,fontWeight:'700'}}>🔀 Manual</button>
                          </>
                        )}
                        <button onClick={()=>actualizarPlan({peligros:peligros.filter(x=>x.id!==p.id)})} style={{background:'none',border:'none',color:'#e63946',cursor:'pointer',fontSize:'16px'}}>×</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ETAPA 6 — PCCs + ARIA límites */}
          {etapaActiva===6&&(
            <div>
              {peligrosPCC.length>0&&(
                <div style={{...card,background:oscuro?'#1a0505':'#fff5f5',border:'1px solid #e6394640',marginBottom:'1rem'}}>
                  <p style={{fontSize:'12px',fontWeight:'700',color:'#e63946',margin:'0 0 6px'}}>🎯 PCCs identificados por árbol de decisiones:</p>
                  {peligrosPCC.map(p=><p key={p.id} style={{fontSize:'12px',color:texto,margin:'2px 0'}}>• {p.descripcion} — {p.etapa}</p>)}
                </div>
              )}
              <div style={card}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <div><span style={lbl}>Peligro vinculado</span><select value={formPCC.peligroId} onChange={e=>setFormPCC(f=>({...f,peligroId:e.target.value,etapa:peligros.find(p=>String(p.id)===e.target.value)?.etapa||f.etapa}))} style={inp}><option value="">— Seleccioná —</option>{peligros.map(p=><option key={p.id} value={p.id}>{p.descripcion}</option>)}</select></div>
                  <div><span style={lbl}>Etapa *</span><input value={formPCC.etapa} onChange={e=>setFormPCC(f=>({...f,etapa:e.target.value}))} style={inp}/></div>
                  <div><span style={lbl}>Límite crítico *</span><input value={formPCC.limCritico} onChange={e=>setFormPCC(f=>({...f,limCritico:e.target.value}))} style={inp} placeholder="Ej: T° ≥ 72°C"/></div>
                  <div><span style={lbl}>Límite operacional</span><input value={formPCC.limOperacional} onChange={e=>setFormPCC(f=>({...f,limOperacional:e.target.value}))} style={inp}/></div>
                  <div><span style={lbl}>Sistema monitoreo</span><input value={formPCC.monitoreo} onChange={e=>setFormPCC(f=>({...f,monitoreo:e.target.value}))} style={inp}/></div>
                  <div><span style={lbl}>Frecuencia</span><input value={formPCC.frecuencia} onChange={e=>setFormPCC(f=>({...f,frecuencia:e.target.value}))} style={inp}/></div>
                  <div><span style={lbl}>Responsable</span><input value={formPCC.responsable} onChange={e=>setFormPCC(f=>({...f,responsable:e.target.value}))} style={inp}/></div>
                  <div><span style={lbl}>Acción correctiva</span><input value={formPCC.accionCorrectiva} onChange={e=>setFormPCC(f=>({...f,accionCorrectiva:e.target.value}))} style={inp}/></div>
                </div>
                <button onClick={agregarPCC} style={{...btn('#e63946'),marginTop:'12px'}}>+ Establecer PCC</button>
              </div>

              {pccs.map((p,i)=>(
                <div key={p.id} style={{...card,borderLeft:'4px solid #e63946'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div style={{flex:1}}>
                      <div style={{display:'flex',gap:'8px',marginBottom:'6px'}}>
                        <span style={{fontSize:'12px',fontWeight:'800',background:'#fee2e2',color:'#b91c1c',padding:'3px 10px',borderRadius:'8px'}}>🎯 PCC-{i+1}</span>
                        <span style={{fontSize:'13px',fontWeight:'700',color:texto}}>{p.etapa}</span>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                        {[['Límite crítico',p.limCritico,'#e63946'],['Límite operacional',p.limOperacional,'#f59e0b'],['Monitoreo',p.monitoreo,sub],['Frecuencia',p.frecuencia,sub],['Responsable',p.responsable,sub],['Acción correctiva',p.accionCorrectiva,'#22c55e'],['Fundamento',p.fundamentoNormativo,'#7B5BF2']].map(([k,v,c])=>v&&(
                          <div key={k}><p style={{fontSize:'10px',color:sub,margin:'0 0 1px',textTransform:'uppercase',fontWeight:'700'}}>{k}</p><p style={{fontSize:'12px',color:c,fontWeight:'600',margin:0}}>{v}</p></div>
                        ))}
                      </div>
                    </div>
                    <div style={{display:'flex',gap:'6px',marginLeft:'8px'}}>
                      <button onClick={()=>ariaLimites(p)} style={{padding:'4px 8px',background:'#f59e0b22',border:'1px solid #f59e0b',borderRadius:'6px',cursor:'pointer',fontSize:'10px',color:'#f59e0b',fontWeight:'700'}}>🤖 Límites</button>
                      <button onClick={()=>actualizarPlan({pccs:pccs.filter(x=>x.id!==p.id)})} style={{background:'none',border:'none',color:'#e63946',cursor:'pointer',fontSize:'16px'}}>×</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ETAPA 7 */}
          {etapaActiva===7&&(
            <div style={{...card,background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #2A7FFF40',textAlign:'center',padding:'2rem'}}>
              <p style={{fontSize:'48px',margin:'0 0 1rem'}}>📊</p>
              <p style={{fontSize:'15px',fontWeight:'700',color:texto,marginBottom:'8px'}}>Registros de monitoreo</p>
              <p style={{fontSize:'13px',color:sub,marginBottom:'1.5rem'}}>{(planActual.registros||[]).length} registros · {pccs.length} PCCs activos</p>
              <button onClick={()=>setVista('monitoreo')} style={btn()}>📊 Ir al panel de monitoreo</button>
            </div>
          )}

          {/* ETAPA 8 — INFORME GENERAL */}
          {etapaActiva===8&&(
            <div>
              {/* Header del informe */}
              <div style={{...card,background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #2A7FFF40',marginBottom:'1rem'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'12px'}}>
                  <div>
                    <h2 style={{fontSize:'18px',fontWeight:'800',color:texto,margin:'0 0 4px'}}>{planActual.producto}</h2>
                    <p style={{fontSize:'12px',color:sub,margin:0}}>{planActual.codigo} · {planActual.rubro} · v{planActual.version}</p>
                  </div>
                  <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                    <span style={{fontSize:'11px',fontWeight:'700',padding:'4px 12px',borderRadius:'8px',background:planActual.estado==='vigente'?'#dcfce7':planActual.estado==='borrador'?'#fef9c3':'#dbeafe',color:planActual.estado==='vigente'?'#15803d':planActual.estado==='borrador'?'#a16207':'#1d4ed8'}}>
                      {planActual.estado==='vigente'?'✅ VIGENTE':planActual.estado==='borrador'?'📝 BORRADOR':'🔄 EN REVISIÓN'}
                    </span>
                  </div>
                </div>
                {/* KPIs rápidos */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))',gap:'8px'}}>
                  {[
                    ['👥 Equipo',(planActual.equipoHACCP||[]).length+' integrantes','#2A7FFF'],
                    ['🔀 Proceso',(planActual.flujo||[]).length+' etapas','#7B5BF2'],
                    ['⚠️ Peligros',(planActual.peligros||[]).length+' identificados','#f59e0b'],
                    ['🎯 PCCs',(planActual.pccs||[]).length+' establecidos','#e63946'],
                    ['📊 Registros',(planActual.registros||[]).length+' cargados','#22c55e'],
                    ['❌ NC',(planActual.registros||[]).filter(r=>r.conforme==='no').length+' no conformes',(planActual.registros||[]).filter(r=>r.conforme==='no').length>0?'#e63946':'#22c55e'],
                  ].map(([l,v,c])=>(
                    <div key={l} style={{background:cardBg,borderRadius:'10px',padding:'10px',border:`1px solid ${borde}`,textAlign:'center'}}>
                      <p style={{fontSize:'12px',fontWeight:'700',color:c,margin:'0 0 2px'}}>{v}</p>
                      <p style={{fontSize:'10px',color:sub,margin:0}}>{l}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* EQUIPO */}
              {(planActual.equipoHACCP||[]).length>0&&(
                <div style={card}>
                  <h3 style={{fontSize:'13px',fontWeight:'700',color:Q.azul,marginBottom:'10px'}}>👥 Equipo HACCP</h3>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'8px'}}>
                    {(planActual.equipoHACCP||[]).map(m=>(
                      <div key={m.id} style={{padding:'8px 12px',borderRadius:'8px',background:oscuro?'#091840':'#f4f6fb',border:`1px solid ${borde}`}}>
                        <p style={{fontSize:'13px',fontWeight:'600',color:texto,margin:'0 0 2px'}}>{m.nombre}</p>
                        <p style={{fontSize:'11px',color:sub,margin:0}}>{m.rol} · {m.area}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* DESCRIPCIÓN PRODUCTO */}
              <div style={card}>
                <h3 style={{fontSize:'13px',fontWeight:'700',color:Q.azul,marginBottom:'10px'}}>📦 Descripción del producto</h3>
                {[
                  ['Producto',planActual.producto],['Rubro',planActual.rubro],
                  ['Descripción',planActual.descripcion],['Vida útil',planActual.vidaUtil],
                  ['Almacenamiento',planActual.condAlmacenamiento],['Alérgenos',planActual.alergenosDeclarados],
                  ['Uso previsto',planActual.usoP],['Consumidor objetivo',planActual.consumidorObjetivo],
                ].filter(([,v])=>v).map(([k,v])=>(
                  <div key={k} style={{display:'flex',gap:'12px',padding:'6px 0',borderBottom:`0.5px solid ${borde}`}}>
                    <span style={{fontSize:'11px',color:sub,fontWeight:'700',minWidth:'140px',textTransform:'uppercase'}}>{k}</span>
                    <span style={{fontSize:'13px',color:texto}}>{v}</span>
                  </div>
                ))}
              </div>

              {/* FLUJO DE PROCESO */}
              {(planActual.flujo||[]).length>0&&(
                <div style={card}>
                  <h3 style={{fontSize:'13px',fontWeight:'700',color:Q.azul,marginBottom:'10px'}}>🔀 Diagrama de flujo del proceso</h3>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'6px',alignItems:'center'}}>
                    {(planActual.flujo||[]).map((e,i)=>(
                      <div key={e.id} style={{display:'flex',alignItems:'center',gap:'6px'}}>
                        <div style={{padding:'6px 14px',borderRadius:'20px',background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #2A7FFF30',fontSize:'12px',fontWeight:'600',color:texto}}>
                          {i+1}. {e.nombre}
                          {e.parametros&&<span style={{fontSize:'10px',color:sub,marginLeft:'4px'}}>({e.parametros})</span>}
                        </div>
                        {i<(planActual.flujo.length-1)&&<span style={{color:sub,fontSize:'16px'}}>→</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ANÁLISIS DE PELIGROS */}
              {(planActual.peligros||[]).length>0&&(
                <div style={card}>
                  <h3 style={{fontSize:'13px',fontWeight:'700',color:Q.azul,marginBottom:'10px'}}>⚠️ Análisis de peligros identificados</h3>
                  {/* Resumen por tipo */}
                  <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'14px'}}>
                    {TIPOS_PELIGRO.map(t=>{
                      const count = (planActual.peligros||[]).filter(p=>p.tipo===t.id).length;
                      if (!count) return null;
                      return (
                        <div key={t.id} style={{padding:'4px 12px',borderRadius:'20px',background:`${t.color}15`,border:`1px solid ${t.color}40`,fontSize:'11px',fontWeight:'700',color:t.color}}>
                          {t.label}: {count}
                        </div>
                      );
                    })}
                  </div>
                  {(planActual.peligros||[]).map(p=>{
                    const tipo=TIPOS_PELIGRO.find(t=>t.id===p.tipo);
                    const riesgo=riesgoTotal(p.severidad,p.probabilidad);
                    return (
                      <div key={p.id} style={{padding:'10px 12px',borderRadius:'10px',border:`1px solid ${tipo?.color}30`,marginBottom:'8px',background:oscuro?'#091840':'#fafafa',borderLeft:`4px solid ${tipo?.color}`}}>
                        <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'4px'}}>
                          <span style={{fontSize:'10px',fontWeight:'700',background:`${tipo?.color}22`,color:tipo?.color,padding:'1px 7px',borderRadius:'5px'}}>{tipo?.label}</span>
                          <span style={{fontSize:'10px',fontWeight:'700',background:`${riesgo.color}22`,color:riesgo.color,padding:'1px 7px',borderRadius:'5px'}}>{riesgo.label}</span>
                          {p.esPCC&&<span style={{fontSize:'10px',fontWeight:'700',background:'#fee2e2',color:'#b91c1c',padding:'1px 7px',borderRadius:'5px'}}>🎯 PCC</span>}
                          {p.resultadoArbol==='NO_PCC'&&<span style={{fontSize:'10px',fontWeight:'700',background:'#dcfce7',color:'#15803d',padding:'1px 7px',borderRadius:'5px'}}>✅ No PCC</span>}
                          {p.origenARIA&&<span style={{fontSize:'9px',fontWeight:'700',background:'linear-gradient(135deg,#7B5BF2,#1C3FAA)',color:'white',padding:'1px 6px',borderRadius:'4px'}}>ARIA</span>}
                        </div>
                        <p style={{fontSize:'13px',fontWeight:'600',color:texto,margin:'0 0 2px'}}>{p.descripcion}</p>
                        <p style={{fontSize:'11px',color:sub,margin:'0 0 2px'}}>Etapa: {p.etapa} · Sev: {p.severidad} · Prob: {p.probabilidad}</p>
                        {p.causas&&<p style={{fontSize:'11px',color:sub,margin:0}}>Causas: {p.causas}</p>}
                        {p.justificacionARIA&&<p style={{fontSize:'11px',color:'#7B5BF2',margin:'4px 0 0',fontStyle:'italic'}}>🤖 {p.justificacionARIA}</p>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* PCCs */}
              {(planActual.pccs||[]).length>0&&(
                <div style={card}>
                  <h3 style={{fontSize:'13px',fontWeight:'700',color:'#e63946',marginBottom:'10px'}}>🎯 Puntos Críticos de Control</h3>
                  {(planActual.pccs||[]).map((p,i)=>(
                    <div key={p.id} style={{padding:'12px',borderRadius:'12px',border:'2px solid #e6394630',marginBottom:'10px',background:oscuro?'#1a0505':'#fff9f9'}}>
                      <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'10px'}}>
                        <span style={{fontSize:'13px',fontWeight:'800',background:'#fee2e2',color:'#b91c1c',padding:'4px 12px',borderRadius:'8px'}}>🎯 PCC-{i+1}</span>
                        <span style={{fontSize:'14px',fontWeight:'700',color:texto}}>{p.etapa}</span>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                        {[
                          ['Límite crítico',p.limCritico,'#e63946'],
                          ['Límite operacional',p.limOperacional,'#f59e0b'],
                          ['Sistema de monitoreo',p.monitoreo,sub],
                          ['Frecuencia',p.frecuencia,sub],
                          ['Responsable',p.responsable,sub],
                          ['Acción correctiva',p.accionCorrectiva,'#22c55e'],
                          ['Fundamento normativo',p.fundamentoNormativo,'#7B5BF2'],
                        ].filter(([,v])=>v).map(([k,v,c])=>(
                          <div key={k}>
                            <p style={{fontSize:'10px',color:sub,margin:'0 0 2px',textTransform:'uppercase',fontWeight:'700'}}>{k}</p>
                            <p style={{fontSize:'12px',color:c,fontWeight:'600',margin:0}}>{v}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* MONITOREO RESUMEN */}
              {(planActual.registros||[]).length>0&&(
                <div style={card}>
                  <h3 style={{fontSize:'13px',fontWeight:'700',color:Q.azul,marginBottom:'10px'}}>📊 Resumen de monitoreo</h3>
                  {(()=>{
                    const regs = planActual.registros||[];
                    const conformes = regs.filter(r=>r.conforme==='si').length;
                    const noConf = regs.filter(r=>r.conforme==='no').length;
                    const tasa = regs.length>0?((conformes/regs.length)*100).toFixed(0):0;
                    return (
                      <div>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px',marginBottom:'12px'}}>
                          {[['Total',regs.length,'#2A7FFF'],['Conformes',conformes,'#22c55e'],['No conformes',noConf,'#e63946']].map(([l,v,c])=>(
                            <div key={l} style={{textAlign:'center',padding:'10px',borderRadius:'10px',background:oscuro?'#091840':'#f4f6fb',border:`1px solid ${borde}`}}>
                              <p style={{fontSize:'20px',fontWeight:'800',color:c,margin:'0 0 2px'}}>{v}</p>
                              <p style={{fontSize:'10px',color:sub,margin:0}}>{l}</p>
                            </div>
                          ))}
                        </div>
                        <div style={{marginBottom:'8px'}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                            <span style={{fontSize:'11px',color:sub}}>Tasa de conformidad</span>
                            <span style={{fontSize:'11px',fontWeight:'700',color:Number(tasa)>=90?'#22c55e':Number(tasa)>=70?'#f59e0b':'#e63946'}}>{tasa}%</span>
                          </div>
                          <div style={{background:oscuro?'#091840':'#f4f6fb',borderRadius:'6px',height:'10px',overflow:'hidden'}}>
                            <div style={{width:`${tasa}%`,height:'100%',background:Number(tasa)>=90?'#22c55e':Number(tasa)>=70?'#f59e0b':'#e63946',borderRadius:'6px',transition:'width .3s'}}/>
                          </div>
                        </div>
                        {noConf>0&&(
                          <div style={{padding:'8px 12px',borderRadius:'8px',background:oscuro?'#1a0505':'#fff5f5',border:'1px solid #e6394640'}}>
                            <p style={{fontSize:'12px',fontWeight:'700',color:'#e63946',margin:'0 0 4px'}}>⚠️ Últimos no conformes:</p>
                            {regs.filter(r=>r.conforme==='no').slice(-3).map(r=>(
                              <p key={r.id} style={{fontSize:'11px',color:sub,margin:'2px 0'}}>{r.fecha} · {r.pccLabel} · {r.valor} {r.observaciones&&`— ${r.observaciones}`}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* CONCLUSIÓN ARIA */}
              <div style={{...card,background:oscuro?'#0a0a2a':'#f5f3ff',border:'2px solid #7B5BF2',marginTop:'1rem'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                    <span style={{fontSize:'24px'}}>🤖</span>
                    <div>
                      <p style={{fontSize:'14px',fontWeight:'800',color:'#7B5BF2',margin:0}}>ARIA — Conclusión del plan HACCP</p>
                      <p style={{fontSize:'11px',color:sub,margin:0}}>Análisis automático del estado del plan y aptitud para auditoría</p>
                    </div>
                  </div>
                  <button onClick={()=>ariaConclusion(planActual)} disabled={cargandoConclusionARIA}
                    style={{padding:'8px 16px',background:cargandoConclusionARIA?'#7B5BF230':'linear-gradient(135deg,#7B5BF2,#1C3FAA)',border:'none',borderRadius:'10px',cursor:cargandoConclusionARIA?'default':'pointer',fontSize:'12px',color:'white',fontWeight:'700',flexShrink:0}}>
                    {cargandoConclusionARIA?'⏳ Analizando...':'🤖 Generar conclusión'}
                  </button>
                </div>

                {cargandoConclusionARIA&&(
                  <div style={{textAlign:'center',padding:'2rem'}}>
                    <p style={{color:'#7B5BF2',fontWeight:'700'}}>ARIA está analizando el plan completo...</p>
                  </div>
                )}

                {!cargandoConclusionARIA&&!conclusionARIA&&(
                  <div style={{textAlign:'center',padding:'1.5rem',color:sub}}>
                    <p style={{fontSize:'13px'}}>Hacé click en "Generar conclusión" para que ARIA analice el plan completo y emita su diagnóstico.</p>
                  </div>
                )}

                {!cargandoConclusionARIA&&conclusionARIA&&!conclusionARIA.error&&(
                  <div>
                    {/* Puntuación y nivel */}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px',marginBottom:'16px'}}>
                      <div style={{textAlign:'center',padding:'1rem',borderRadius:'12px',background:cardBg,border:`1px solid ${borde}`}}>
                        <p style={{fontSize:'32px',fontWeight:'900',color:'#7B5BF2',margin:'0 0 4px'}}>{conclusionARIA.puntuacionGeneral}</p>
                        <p style={{fontSize:'10px',color:sub,margin:0}}>Puntuación / 100</p>
                      </div>
                      <div style={{textAlign:'center',padding:'1rem',borderRadius:'12px',background:cardBg,border:`1px solid ${borde}`}}>
                        <p style={{fontSize:'14px',fontWeight:'800',color:Q.azulVivo,margin:'0 0 4px'}}>{conclusionARIA.nivelMadurez}</p>
                        <p style={{fontSize:'10px',color:sub,margin:0}}>Nivel de madurez</p>
                      </div>
                      <div style={{textAlign:'center',padding:'1rem',borderRadius:'12px',background:cardBg,border:`1px solid ${conclusionARIA.riesgoGlobal==='Bajo'?'#22c55e':conclusionARIA.riesgoGlobal==='Medio'?'#f59e0b':'#e63946'}`}}>
                        <p style={{fontSize:'14px',fontWeight:'800',color:conclusionARIA.riesgoGlobal==='Bajo'?'#22c55e':conclusionARIA.riesgoGlobal==='Medio'?'#f59e0b':'#e63946',margin:'0 0 4px'}}>{conclusionARIA.riesgoGlobal}</p>
                        <p style={{fontSize:'10px',color:sub,margin:0}}>Riesgo global</p>
                      </div>
                    </div>

                    {/* Aptitud auditoría */}
                    <div style={{padding:'12px 16px',borderRadius:'10px',background:conclusionARIA.aptitudAuditoria?(oscuro?'#0a1f0a':'#f0fdf4'):(oscuro?'#1a0505':'#fff5f5'),border:`1.5px solid ${conclusionARIA.aptitudAuditoria?'#22c55e':'#e63946'}`,marginBottom:'14px'}}>
                      <p style={{fontSize:'13px',fontWeight:'700',color:conclusionARIA.aptitudAuditoria?'#22c55e':'#e63946',margin:'0 0 4px'}}>
                        {conclusionARIA.aptitudAuditoria?'✅ Plan APTO para auditoría':'⚠️ Plan NO apto para auditoría todavía'}
                      </p>
                      <p style={{fontSize:'12px',color:sub,margin:0}}>{conclusionARIA.comentarioAuditoria}</p>
                    </div>

                    {/* Conclusión general */}
                    <div style={{padding:'12px 16px',borderRadius:'10px',background:oscuro?'#0a1830':'#f0f7ff',border:'1px solid #2A7FFF30',marginBottom:'14px'}}>
                      <p style={{fontSize:'11px',color:sub,fontWeight:'700',margin:'0 0 6px',textTransform:'uppercase'}}>Conclusión general</p>
                      <p style={{fontSize:'13px',color:texto,margin:0,lineHeight:1.6}}>{conclusionARIA.conclusionGeneral}</p>
                    </div>

                    {/* Fortalezas y brechas */}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'14px'}}>
                      <div style={{padding:'12px',borderRadius:'10px',background:oscuro?'#0a1f0a':'#f0fdf4',border:'1px solid #22c55e30'}}>
                        <p style={{fontSize:'11px',color:'#22c55e',fontWeight:'700',margin:'0 0 8px',textTransform:'uppercase'}}>✅ Fortalezas</p>
                        {(conclusionARIA.fortalezas||[]).map((f,i)=>(
                          <p key={i} style={{fontSize:'12px',color:texto,margin:'0 0 4px'}}>• {f}</p>
                        ))}
                      </div>
                      <div style={{padding:'12px',borderRadius:'10px',background:oscuro?'#1a0505':'#fff5f5',border:'1px solid #e6394630'}}>
                        <p style={{fontSize:'11px',color:'#e63946',fontWeight:'700',margin:'0 0 8px',textTransform:'uppercase'}}>⚠️ Brechas</p>
                        {(conclusionARIA.brechas||[]).map((b,i)=>(
                          <p key={i} style={{fontSize:'12px',color:texto,margin:'0 0 4px'}}>• {b}</p>
                        ))}
                      </div>
                    </div>

                    {/* Recomendaciones */}
                    {(conclusionARIA.recomendaciones||[]).length>0&&(
                      <div>
                        <p style={{fontSize:'11px',color:sub,fontWeight:'700',margin:'0 0 8px',textTransform:'uppercase'}}>📋 Recomendaciones</p>
                        {(conclusionARIA.recomendaciones||[]).map((r,i)=>(
                          <div key={i} style={{display:'flex',gap:'10px',alignItems:'flex-start',padding:'8px 12px',borderRadius:'8px',background:oscuro?'#091840':'#f4f6fb',border:`1px solid ${borde}`,marginBottom:'6px'}}>
                            <span style={{fontSize:'10px',fontWeight:'700',padding:'2px 8px',borderRadius:'6px',background:r.prioridad==='alta'?'#fee2e2':r.prioridad==='media'?'#fef9c3':'#dcfce7',color:r.prioridad==='alta'?'#b91c1c':r.prioridad==='media'?'#a16207':'#15803d',flexShrink:0,marginTop:'1px'}}>
                              {r.prioridad?.toUpperCase()}
                            </span>
                            <div style={{flex:1}}>
                              <p style={{fontSize:'12px',color:texto,fontWeight:'600',margin:'0 0 2px'}}>{r.accion}</p>
                              <p style={{fontSize:'11px',color:sub,margin:0}}>Plazo: {r.plazo}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!cargandoConclusionARIA&&conclusionARIA?.error&&(
                  <div style={{textAlign:'center',padding:'1rem'}}>
                    <p style={{color:'#e63946',fontSize:'13px'}}>{conclusionARIA.error}</p>
                  </div>
                )}
              </div>

              {/* Botones finales */}
              <div style={{display:'flex',gap:'10px',marginTop:'1rem'}}>
                <button onClick={ariaReporte} disabled={cargandoARIA}
                  style={{flex:1,padding:'12px',background:'#22c55e',color:'white',border:'none',borderRadius:'12px',cursor:'pointer',fontSize:'13px',fontWeight:'700'}}>
                  📄 Exportar reporte PDF completo
                </button>
                <button onClick={()=>setVista('monitoreo')}
                  style={{flex:1,padding:'12px',background:Q.azulVivo,color:'white',border:'none',borderRadius:'12px',cursor:'pointer',fontSize:'13px',fontWeight:'700'}}>
                  📊 Ir al monitoreo
                </button>
              </div>
            </div>
          )}

          <div style={{display:'flex',justifyContent:'space-between',marginTop:'1rem'}}>
            {etapaActiva>1&&<button onClick={()=>setEtapaActiva(e=>e-1)} style={{...btn('#8E93A1'),padding:'10px 20px'}}>← Anterior</button>}
            {etapaActiva<8&&<button onClick={()=>setEtapaActiva(e=>e+1)} style={{...btn(),marginLeft:'auto',padding:'10px 20px'}}>Siguiente →</button>}
          </div>
        </div>
      </div>
    );
  }

  // NUEVO
  if (vista==='nuevo') return (
    <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif'}}>
      <Header titulo="Nuevo Plan HACCP" onBack={()=>setVista('listado')}/>
      <div style={{padding:'0 2rem 2rem',maxWidth:'700px',margin:'0 auto'}}>
        <div style={card}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <div style={{gridColumn:'1/-1'}}><span style={lbl}>Nombre del producto *</span><input value={form.producto} onChange={e=>setForm(f=>({...f,producto:e.target.value}))} style={inp} placeholder="Ej: Caja packaging secundario cereal"/></div>
            <div><span style={lbl}>Rubro</span><select value={form.rubro} onChange={e=>setForm(f=>({...f,rubro:e.target.value}))} style={inp}>{RUBROS_DEFAULT.map(r=><option key={r} value={r}>{r}</option>)}</select></div>
            <div><span style={lbl}>Vida útil</span><input value={form.vidaUtil} onChange={e=>setForm(f=>({...f,vidaUtil:e.target.value}))} style={inp} placeholder="Ej: 24 meses"/></div>
            <div style={{gridColumn:'1/-1'}}><span style={lbl}>Descripción breve</span><textarea rows={2} value={form.descripcion} onChange={e=>setForm(f=>({...f,descripcion:e.target.value}))} style={{...inp,resize:'none'}}/></div>
          </div>
          <button onClick={crearPlan} style={{...btn(),marginTop:'1.5rem',width:'100%',padding:'14px',fontSize:'14px'}}>🚀 Crear plan HACCP</button>
        </div>
      </div>
    </div>
  );

  // CONFIRMAR ELIMINAR
  if (confirmEliminar) return (
    <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:cardBg,borderRadius:'16px',padding:'2rem',maxWidth:'360px',width:'90%',border:`1px solid ${borde}`,textAlign:'center'}}>
        <p style={{fontSize:'36px',marginBottom:'1rem'}}>⚠️</p>
        <p style={{fontSize:'15px',fontWeight:'600',color:texto,marginBottom:'.5rem'}}>¿Eliminar este plan HACCP?</p>
        <p style={{fontSize:'13px',color:sub,marginBottom:'1.5rem'}}>Esta acción no se puede deshacer.</p>
        <div style={{display:'flex',gap:'10px',justifyContent:'center'}}>
          <button onClick={()=>setConfirmEliminar(null)} style={{padding:'10px 20px',background:'none',border:`1px solid ${borde}`,borderRadius:'10px',cursor:'pointer',color:texto,fontSize:'13px'}}>Cancelar</button>
          <button onClick={()=>eliminarPlan(confirmEliminar)} style={{padding:'10px 20px',background:'#e63946',border:'none',borderRadius:'10px',cursor:'pointer',color:'white',fontSize:'13px',fontWeight:'600'}}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  // LISTADO
  const estadoColor2 = { borrador:'#f59e0b', vigente:'#22c55e', revision:'#3b82f6' };
  return (
    <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif'}}>
      <div style={{background:navBg,padding:'1rem 1.5rem',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:100,boxShadow:'0 2px 12px rgba(0,0,0,.3)'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <button onClick={onVolver} style={{background:'rgba(255,255,255,0.1)',border:'none',color:'white',padding:'6px 14px',borderRadius:'10px',cursor:'pointer',fontSize:'13px'}}>← Menú</button>
          <BotonInstructivo moduloKey="haccp" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="haccp" oscuro={oscuro} />}
          <span style={{color:'white',fontWeight:'700',fontSize:'16px'}}>🧫 HACCP</span>
        </div>
        {esAdmin&&<button onClick={()=>setVista('nuevo')} style={{padding:'6px 14px',background:Q.azulVivo,color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>+ Nuevo plan</button>}
      </div>
      <div style={{padding:'1.2rem 1.5rem',maxWidth:'860px',margin:'0 auto'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:'10px',marginBottom:'1.5rem'}}>
          {[['Total',planes.length,'#2A7FFF'],['Vigentes',planes.filter(p=>p.estado==='vigente').length,'#22c55e'],['Borradores',planes.filter(p=>p.estado==='borrador').length,'#f59e0b'],['PCCs totales',planes.reduce((a,p)=>a+(p.pccs||[]).length,0),'#e63946']].map(([l,v,c])=>(
            <div key={l} style={{background:cardBg,borderRadius:'14px',padding:'1rem',border:`1px solid ${borde}`,textAlign:'center'}}>
              <p style={{fontSize:'24px',fontWeight:'800',color:c,margin:'0 0 4px'}}>{v}</p>
              <p style={{fontSize:'11px',color:sub,margin:0,fontWeight:'600'}}>{l}</p>
            </div>
          ))}
        </div>
        {planes.length===0?(
          <div style={{textAlign:'center',padding:'5rem 2rem'}}>
            <p style={{fontSize:'48px'}}>🧫</p>
            <p style={{fontSize:'15px',color:texto,fontWeight:'600',marginTop:'1rem'}}>Sin planes HACCP</p>
            <p style={{fontSize:'13px',color:sub}}>{esAdmin?'Tocá "+ Nuevo plan" para comenzar.':'Sin planes cargados.'}</p>
          </div>
        ):planes.map(p=>(
          <div key={p.id} onClick={()=>{setPlanActual(p);setEtapaActiva(1);setVista('detalle');}}
            style={{background:cardBg,borderRadius:'14px',padding:'1.2rem 1.5rem',border:`1.5px solid ${borde}`,marginBottom:'10px',cursor:'pointer',transition:'box-shadow 0.15s'}}
            onMouseEnter={e=>e.currentTarget.style.boxShadow=`0 4px 20px ${Q.azulVivo}20`}
            onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div style={{flex:1}}>
                <div style={{display:'flex',gap:'8px',marginBottom:'4px',flexWrap:'wrap'}}>
                  <span style={{fontWeight:'700',color:Q.azulVivo,fontSize:'13px'}}>{p.codigo}</span>
                  <span style={{fontSize:'13px',color:texto,fontWeight:'600'}}>{p.producto}</span>
                  <span style={{fontSize:'10px',fontWeight:'700',padding:'2px 8px',borderRadius:'6px',background:`${estadoColor2[p.estado]}22`,color:estadoColor2[p.estado]}}>{p.estado?.toUpperCase()}</span>
                </div>
                <p style={{fontSize:'12px',color:sub,margin:'0 0 4px'}}>{p.rubro} · v{p.version}</p>
                <div style={{display:'flex',gap:'12px',fontSize:'11px',color:sub}}>
                  <span>👥 {(p.equipoHACCP||[]).length}</span>
                  <span>⚠️ {(p.peligros||[]).length} peligros</span>
                  <span>🎯 {(p.pccs||[]).length} PCCs</span>
                  <span>📊 {(p.registros||[]).length} registros</span>
                </div>
              </div>
              <span style={{fontSize:'11px',color:sub}}>{p.fechaCreacion?.slice(0,10)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}